// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { Fragment } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { useRoute, useLocation } from 'preact-iso';
import {
  api,
  type IssuedInvite,
  type SharedParticipantStatus,
  type SharedPendingTx,
  type SharedPendingTxStatus,
  type SharedVaultActivationBundle,
  type SharedVaultDetailResponse,
  type SharedVaultParticipant,
  type SharedVaultRollingLimit,
  type SharedVaultState,
  type SharedWithdrawalOutput,
  type WalletTx,
} from '../api.js';
import { activationFinishing } from '../shared-vault-accept-flow.js';
import {
  CLAIM_LINK_BEARER_WARNING_ONE,
  ClaimLinkCard,
} from '../components/claim-link-card.js';
import {
  buildSharedVaultSpec,
  descriptorExportVisible,
  sharedVaultSpecFilename,
} from '../descriptor-export.js';
import { verifierSharedVaultUrlForNetwork } from '../verifier-shared-origins.js';
import { useSession } from '../session-context.js';
import { useSharedVaultPolicyEnabled } from '../spa-config.js';
import {
  formatBtc,
  formatDate,
  formatSats,
  parseBtcToSats,
  truncate,
} from '../format.js';
import { balancePanelModel, awaitingConfirmationHint } from '../balance.js';
import { TransactionList } from '../components/transaction-list.js';
import {
  FeeSelector,
  feeSelectionError,
  feeTierForRequest,
  initialFeeSelection,
  ladderTier,
  rateMovedMaterially,
  rateMovedNotice,
  selectedFeeRate,
  type FeeLadderView,
  type FeeSelection,
} from '../components/fee-selector.js';
import { BalancePanel } from '../components/balance-panel.js';
import { DepositAddressField } from '../components/deposit-address-field.js';
import { AddressReveal } from '../components/address-reveal.js';
import {
  quorumLabel,
  totalKeys,
  sharedVaultStateLabel,
  sharedVaultStateTone,
  formatRollingLimit,
} from './shared-vaults.js';
import '../styles/home.css';
import '../styles/shared-vaults.css';
import '../styles/wallet.css';
import '../styles/vault-detail.css';

/** States that never change again — stop the refresh poll once reached. */
const TERMINAL_STATES: ReadonlySet<SharedVaultState> = new Set<SharedVaultState>([
  'REJECTED',
  'DELETED',
]);

/** COB-485: settle-time before re-quoting the fee ladder after the output set
 *  changes, so typing an address does not fan out one request per keystroke. */
const FEE_LADDER_DEBOUNCE_MS = 400;

/** COB-485: how often the open send form re-prices, in ms. Twice the server's
 *  30s estimate cache, so a poll costs at most one upstream call per user. */
const FEE_LADDER_REFRESH_MS = 60_000;

// ── Pure helpers (exported for unit tests) ────────────────────────────

/** The caller's own participant row.
 *
 *  Keyed on the server-computed `is_me` — the participant row's own identity
 *  pubkey compared against the caller's session identity, decided where both
 *  facts actually live. No email, no session state, nothing to normalise: this
 *  is the one field on the roster that cannot be absent, stale, or ambiguous.
 *
 *  It deliberately does NOT match on `member_email`. That field is a
 *  best-effort display aid — the backend resolves the whole roster's emails in
 *  one lookup and renders the roster *without* them if it fails — so keying
 *  "me" on it made a transient lookup failure indistinguishable from "you are
 *  not in this vault", silently withdrawing the initiator controls and the
 *  acknowledge CTA from a member who is plainly there. Identity is not a
 *  display concern; the two are now separated.
 *
 *  A caller with no claimed slot has no row at all, which is correct: pre-claim
 *  they are not a participant, only a link-holder. */
export function findMyParticipant(
  participants: SharedVaultParticipant[],
): SharedVaultParticipant | null {
  return participants.find((p) => p.is_me) ?? null;
}

/** Whether the caller is the vault's initiator (the only party who may
 *  regenerate or remove pending invites). */
export function isInitiator(participants: SharedVaultParticipant[]): boolean {
  return findMyParticipant(participants)?.is_initiator ?? false;
}

/** Whether the caller still owes an activation acknowledgement: the vault is
 *  sealed (`PROPOSED`) and the caller is an accepted participant who has not
 *  yet acknowledged the descriptor. Drives the "Review & sign" CTA. */
export function canAcknowledge(vault: SharedVaultDetailResponse): boolean {
  if (vault.state !== 'PROPOSED') return false;
  const me = findMyParticipant(vault.participants);
  return me != null && me.status === 'ACCEPTED' && !me.acknowledged;
}

/** Count of participants who have acknowledged the sealed descriptor. */
export function acknowledgedCount(participants: SharedVaultParticipant[]): number {
  return participants.filter((p) => p.acknowledged).length;
}

/** COB-486: whether the vault's deposit / receive address may be surfaced.
 *  The backend populates `deposit_address` at seal time (`PENDING_INVITES` →
 *  `PROPOSED`, i.e. the roster filled), but deposits stay disabled until every
 *  participant has acknowledged and signed the descriptor (`ACTIVE`). Showing
 *  the address any earlier invites deposits into a vault that may never
 *  activate, so the gate is the state, not merely the field being on the wire.
 *  `EMPTY` (post-active, balance drained) still shows it: that is the vault's
 *  real receive address and must not vanish once funds leave. */
export function depositAddressVisible(vault: SharedVaultDetailResponse): boolean {
  if (!vault.deposit_address) return false;
  return vault.state === 'ACTIVE' || vault.state === 'EMPTY';
}

/** "X of N signed" — the activation progress line. COB-486: phase B is called
 *  **Sign** in user-facing copy (phase A is **Consent**), so the count reads
 *  "signed" even though the wire field is still `acknowledged`. */
export function activationProgressLabel(acknowledged: number, n: number): string {
  return `${acknowledged} of ${n} signed`;
}

/** An invited (not-yet-claimed) non-initiator slot the initiator can mint a new
 *  claim link for, or remove, while the vault is still collecting invites
 *  (PENDING_INVITES). */
export function canManageInvite(participant: SharedVaultParticipant): boolean {
  return participant.status === 'INVITED' && !participant.is_initiator;
}

/** COB-543: how a roster row names its holder.
 *
 *  A claimed slot shows the claimant's ACCOUNT email — the identity claim the
 *  platform can vouch for, and the one co-owners check each other against. An
 *  unclaimed slot has no person yet, only a live link, so it falls back to the
 *  initiator's cosmetic label; and where there is no label either, it says so
 *  plainly rather than inventing a name from the `slot_id`. Never assert an
 *  identity for a slot nobody has claimed. */
export function participantDisplayName(
  participant: SharedVaultParticipant,
): { text: string; muted: boolean } {
  if (participant.member_email) {
    return { text: participant.member_email, muted: false };
  }
  if (participant.label) return { text: participant.label, muted: false };
  return { text: 'Awaiting claim', muted: true };
}

/** COB-486: the roster tag for a participant's phase-A state, in the user's
 *  vocabulary rather than the wire enum. Phase A is **Consent**, so an
 *  `ACCEPTED` slot reads "Consented" and a `REJECTED` one "Declined"; the
 *  request/response fields keep their names. Exhaustive over
 *  `SharedParticipantStatus` so a new wire status is a type error, not a raw
 *  enum leaking into the UI. */
export function participantStatusLabel(status: SharedParticipantStatus): string {
  switch (status) {
    case 'INVITED':
      return 'Invited';
    case 'ACCEPTED':
      return 'Consented';
    case 'REJECTED':
      return 'Declined';
  }
}

// COB-543: the in-app accept (COB-337) and its two message helpers are gone
// with the email invite model they belonged to. That path let a caller with a
// verified account email claim their slot from this page without a token,
// because the slot named an address the server could match them against. A slot
// names nobody now — the claim link's token is the sole credential — so the
// claim page is the one and only way in, and a pending invitee never reaches
// this page at all (they are not a member until they claim).

// ── Withdrawal form helpers (B5) ──────────────────────────────────────

/** One row of the send form — strings as typed; converted on submit. */
export interface SendRow {
  address: string;
  amount: string;
}

export function blankSendRow(): SendRow {
  return { address: '', amount: '' };
}

/** A row the user has not touched at all (ignored on submit). */
export function sendRowIsBlank(row: SendRow): boolean {
  return row.address.trim() === '' && row.amount.trim() === '';
}

/** A BTC amount: a positive decimal that converts to a whole, positive satoshi
 *  count. Parsed via `parseBtcToSats` — the same conversion the personal wallet's
 *  send input uses (`wallet.tsx` `SendCard`) — which returns `NaN` on bad input
 *  and `Math.round`s to integer sats. Validating the *converted sat value* (not
 *  the raw string) means the amount checked here is byte-for-byte the integer
 *  `amount_sats` that `buildSendOutputs` emits and the D3 withdrawal intent signs:
 *  no float drift, no sub-satoshi precision leaking past the form. */
export function isValidWithdrawalAmount(amount: string): boolean {
  const sats = parseBtcToSats(amount);
  return Number.isSafeInteger(sats) && sats > 0;
}

export function sendRowIsValid(row: SendRow): boolean {
  return row.address.trim().length > 0 && isValidWithdrawalAmount(row.amount);
}

/** Convert the non-blank, valid form rows into the request output set. The BTC
 *  amount is converted to integer sats with the same `parseBtcToSats` that
 *  `isValidWithdrawalAmount` validated it with, so the emitted `amount_sats` is
 *  exactly the value that was checked and that the D3 intent will sign. */
export function buildSendOutputs(rows: SendRow[]): SharedWithdrawalOutput[] {
  return rows
    .filter((r) => !sendRowIsBlank(r) && sendRowIsValid(r))
    .map((r) => ({ address: r.address.trim(), amount_sats: parseBtcToSats(r.amount) }));
}

/** Whether the send form may be submitted: an ACTIVE vault, a usable fee
 *  selection, and at least one non-blank row, with every non-blank row valid.
 *
 *  COB-485: the fee half is no longer "is this string a positive number" —
 *  the selector resolves the chosen option (or custom value) against the served
 *  ladder and hands back a blocking reason or `null`, so the floor, the
 *  ceiling, the AR-10 fee cap and the sub-minimum acknowledgement all gate the
 *  button through one channel. */
export function canInitiateWithdrawal(
  state: SharedVaultState,
  rows: SendRow[],
  feeError: string | null,
): boolean {
  if (state !== 'ACTIVE') return false;
  if (feeError !== null) return false;
  const nonBlank = rows.filter((r) => !sendRowIsBlank(r));
  return nonBlank.length > 0 && nonBlank.every(sendRowIsValid);
}

/** "X of M signatures" — the per-withdrawal collection progress. */
export function withdrawalProgressLabel(collected: number, quorum: number): string {
  return `${collected} of ${quorum} signature${quorum === 1 ? '' : 's'}`;
}

/** The inline validation message when the Create-withdrawal guard blocks a
 *  submit (COB-352): the guard mirrors `canInitiateWithdrawal`'s two failure
 *  axes (no valid output row, and/or an unusable fee selection) and names
 *  exactly what is missing — replacing the old silent `return` that made the
 *  button read as a no-op.
 *
 *  COB-485: the fee half now passes through the selector's own reason
 *  verbatim ("At least 2 sat/vB. …", "Confirm you understand…") instead of
 *  the old catch-all "Enter a valid fee rate (sat/vB)", which named the field
 *  but never the problem. */
export function sendValidationMessage(
  rows: SendRow[],
  feeError: string | null,
): string {
  const haveOutputs = buildSendOutputs(rows).length > 0;
  if (!haveOutputs && feeError !== null) {
    return 'Enter a recipient address and a positive amount, then choose a fee rate.';
  }
  if (!haveOutputs) {
    return 'Enter a recipient address and a positive amount.';
  }
  return feeError ?? 'Choose a fee rate.';
}

/** A pending tx is still collecting co-signer partials. Status-only: it is the
 *  row-level "this withdrawal is still open" check, NOT the per-caller button
 *  gate — that layers `!my_signed` on top (`pendingTxAwaitingMySignature`). */
export function pendingTxIsSignable(tx: SharedPendingTx): boolean {
  return tx.status === 'COLLECTING';
}

/** The "Review & sign" button gate (COB-352): the withdrawal is still
 *  COLLECTING AND this caller has not yet contributed a partial. The withdrawal
 *  stays COLLECTING until quorum, so gating on status alone left a live button
 *  on a row the caller had already signed — the re-click was an idempotent
 *  no-op, so the button "did nothing". `my_signed` is backend-authoritative. */
export function pendingTxAwaitingMySignature(tx: SharedPendingTx): boolean {
  return pendingTxIsSignable(tx) && !tx.my_signed;
}

/** The non-interactive signed state (COB-352): the caller has signed a
 *  still-COLLECTING withdrawal and is now waiting on the remaining co-signers. */
export function pendingTxSignedByMe(tx: SharedPendingTx): boolean {
  return pendingTxIsSignable(tx) && tx.my_signed;
}

/** "You've signed — awaiting co-signers (N of M signatures)" — the signed-state
 *  line, reusing `withdrawalProgressLabel` for the collected/quorum counts. */
export function pendingTxSignedLabel(tx: SharedPendingTx): string {
  return `You've signed — awaiting co-signers (${withdrawalProgressLabel(
    tx.collected,
    tx.quorum_m,
  )})`;
}

/** COB-362: the count-free acknowledgement shown inline after a successful
 *  co-sign. The live "N of M signatures" progress belongs to the state-driven
 *  signed line (`pendingTxSignedLabel`), which the 5s poll keeps current — so
 *  this banner never freezes a stale count, and the poll clears it once the row
 *  leaves COLLECTING (`signBannerResolvedByPoll`).
 *
 *  COB-485: the FINALIZED arm no longer says "finalised and queued to
 *  broadcast". "Finalised" is our internal word for "the signed transaction has
 *  been assembled", which a member reads as "done"; and the transaction is not
 *  on the network at that point — the A8 watcher hands it to a node on a later
 *  pass and retries forever if the node rejects it, which is precisely how the
 *  2026-08-05 stuck withdrawal went unnoticed. The copy now says what has
 *  happened (the last signature landed) and what is happening (we are sending
 *  it), and claims nothing about the network having accepted it. */
export function signSuccessMessage(state: 'COLLECTING' | 'FINALIZED'): string {
  return state === 'FINALIZED'
    ? 'Signed — that was the last signature needed. Cobault is now sending this to the Bitcoin network.'
    : 'Signed.';
}

/** COB-485: the human label for a withdrawal's internal status.
 *
 *  Render-layer only — the wire values (`COLLECTING` / `FINALIZED` /
 *  `BROADCAST` / `CANCELLED`) are unchanged and stay the thing every predicate
 *  in this file switches on. The badge used to print the raw enum, which leaked
 *  internal vocabulary and, worse, mis-stated the lifecycle: `FINALIZED` reads
 *  as "finished" but means "fully signed and **not yet on the network**" — the
 *  watcher broadcasts on a later pass and, if the node rejects it, retries
 *  forever with the row still reading FINALIZED (COB-479 / 2026-08-05).
 *
 *  The mapping is deliberately conservative about the network:
 *    COLLECTING → still gathering signatures.
 *    FINALIZED  → signed, ours to send, NOT yet handed over. Never "submitted"
 *                 or "broadcast" — both would claim the network has it.
 *    BROADCAST  → a node accepted it. NOT "confirmed": there is no CONFIRMED
 *                 status (the row reads BROADCAST forever, see migration 0062),
 *                 so any wording about confirmation would eventually be a lie.
 *    CANCELLED  → terminal; `pendingTxCancelledLabel` carries the detail.
 *
 *  Exhaustive switch with no `default`, matching `pendingTxStatusTone`. */
export function pendingTxStatusLabel(status: SharedPendingTxStatus): string {
  switch (status) {
    case 'COLLECTING':
      return 'Collecting signatures';
    case 'FINALIZED':
      return 'Signed — awaiting send';
    case 'BROADCAST':
      return 'Sent to the network';
    case 'CANCELLED':
      return 'Cancelled';
  }
}

/** COB-485 (Ticket C): the fee line every co-signer sees before signing —
 *  sats first (the unit a fee is actually denominated in), BTC second (the unit
 *  the amounts on the row use), and the sat/vB rate the initiator chose.
 *
 *  A shared vault has no hot wallet, so the fee comes straight out of vault
 *  value (AR-3: `fee = Σ inputs − Σ outputs`). It is part of what a signature
 *  authorises, and before this the co-signer surface showed only a rounded BTC
 *  figure and no rate at all.
 *
 *  A withdrawal created before migration 0063 has no recorded rate. It says so
 *  rather than dividing `fee_sats` by an estimated size: that division is lossy
 *  under the 250-sat relay-floor clamp (every sub-floor rate yields the same
 *  fee), so a derived figure would be most wrong in exactly the case a
 *  co-signer most needs it to be right. */
export function pendingTxFeeSummary(tx: SharedPendingTx): string {
  const rate =
    tx.fee_rate_sat_vb == null
      ? 'rate not recorded'
      : `${tx.fee_rate_sat_vb} sat/vB`;
  return `Network fee ${formatSats(tx.fee_sats)} sats · ${formatBtc(
    tx.fee_sats,
  )} BTC · ${rate}`;
}

/** COB-485 (Ticket C): the advisory shown when a still-open withdrawal was
 *  built at a rate now below the cheapest tier the server is currently serving.
 *
 *  **Warns, never blocks.** The sign button is untouched. Refusing to sign a
 *  stale-rate withdrawal would put a moving number between co-owners and their
 *  own funds, and the members already hold the only real remedy (cancel and
 *  re-initiate — COB-461). This exists because a shared-vault PSBT commits to a
 *  fixed fee with no RBF/CPFP escape, and human quorum collection is slow: a
 *  rate that was comfortable when the withdrawal was created can be well under
 *  the floor by the time the last co-signer arrives, which is the shape of the
 *  2026-08-05 incident.
 *
 *  `null` (say nothing) in every case where the comparison would be a guess:
 *    - a terminal row — BROADCAST is already the network's problem and
 *      CANCELLED is going nowhere, and neither can be cancelled + re-initiated,
 *      so the advice would be unactionable;
 *    - a pre-0063 row with no recorded rate;
 *    - no ladder fetched yet, or a ladder with no `minimum` tier. */
export function pendingTxFeeRateWarning(
  tx: SharedPendingTx,
  ladder: FeeLadderView | null,
): string | null {
  if (pendingTxIsTerminal(tx)) return null;
  const rate = tx.fee_rate_sat_vb;
  if (rate == null) return null;
  const minimum = ladderTier(ladder, 'minimum');
  if (!minimum) return null;
  if (rate >= minimum.sat_vb) return null;
  return `This withdrawal was created at ${rate} sat/vB — below the cheapest current estimate of ${minimum.sat_vb} sat/vB — so it may no longer confirm. You can still sign it. A shared-vault withdrawal can't be re-fee'd once it's signed, so if it stalls the way out is to cancel it and start a new one at the current rate.`;
}

/** COB-485: the Send-form hint above the recipient rows.
 *
 *  The previous wording — "Every co-signer verifies the recipients and amounts
 *  in their own browser before signing" — was factually wrong. Signing stops at
 *  the vault's threshold: on a 2-of-3, the transaction is assembled and sent as
 *  soon as the second signature lands, and the third co-owner may never look at
 *  it. The out-of-band guidance is kept and is now motivated rather than
 *  merely asserted — it matters *because* the reviewers are only the ones who
 *  happen to sign first. */
export function sendCosignerHint(quorumM: number, n: number): string {
  return `Each co-signer verifies the recipients and amounts in their own browser before signing — but signing stops at this vault's ${quorumM}-of-${n} threshold, so the co-owners who sign first are the only ones who will review it. Confirm destinations out of band.`;
}

/** COB-362: whether a *success* sign-banner should be reconciled away against
 *  fresh poll data — the withdrawal it referenced has left COLLECTING
 *  (finalised / broadcast) or no longer exists. Error banners are never touched
 *  (a failed-sign message clears only on the next sign attempt). Invariant: no
 *  count/waiting acknowledgement outlives COLLECTING. */
export function signBannerResolvedByPoll(
  banner: { id: string; kind: 'error' | 'success' } | null,
  pending: SharedPendingTx[],
): boolean {
  if (!banner || banner.kind !== 'success') return false;
  const row = pending.find((t) => t.pending_tx_id === banner.id);
  return !row || row.status !== 'COLLECTING';
}

/** COB-362: whether the *success* create-banner ("Withdrawal created — waiting
 *  for co-signers to sign") should be reconciled away — its withdrawal row is
 *  now visible in the poll, so the row's own status badge + signed-state line
 *  own the display and the transient banner must not linger (it would otherwise
 *  read "waiting for co-signers" over a FINALIZED / BROADCAST row). Error
 *  banners are left alone. */
export function sendBannerResolvedByPoll(
  banner: { kind: 'error' | 'success'; id?: string } | null,
  pending: SharedPendingTx[],
): boolean {
  if (!banner || banner.kind !== 'success') return false;
  if (banner.id != null) return pending.some((t) => t.pending_tx_id === banner.id);
  // Defensive: a success banner with no captured id clears once any row lands.
  return pending.length > 0;
}

/** Badge tone for a pending withdrawal's status (COB-356) — `collecting` (still
 *  gathering partials → amber/warning), `finalized` (quorum reached → green/
 *  success), `broadcast` (queued/sent to the network → teal/info), or
 *  `cancelled` (COB-461 — called off; NEUTRAL, since a cancellation is neither
 *  a success nor an error, and tinting it red would read as a failure the
 *  member did not cause). Drives the `.shared-vault-badge--<tone>` modifier,
 *  mirroring `sharedVaultStateTone`'s precedent: an exhaustive switch over the
 *  status union with no `default`. */
export function pendingTxStatusTone(
  status: SharedPendingTxStatus,
): 'collecting' | 'finalized' | 'broadcast' | 'cancelled' {
  switch (status) {
    case 'COLLECTING':
      return 'collecting';
    case 'FINALIZED':
      return 'finalized';
    case 'BROADCAST':
      return 'broadcast';
    case 'CANCELLED':
      return 'cancelled';
  }
}

/** COB-362: a pending withdrawal is terminal — done and no longer actionable —
 *  when it has been BROADCAST to the network, or (COB-461) CANCELLED by a
 *  member. COLLECTING/FINALIZED rows still change (finalise, then broadcast).
 *  Keyed on `status` only: `verifier_state` is an ORTHOGONAL dimension (a
 *  verifier-REFUSED row is still status COLLECTING and a human quorum can act
 *  on it), so it never gates this filter. Drives the "hide closed withdrawals
 *  by default" toggle. */
export function pendingTxIsTerminal(tx: SharedPendingTx): boolean {
  return tx.status === 'BROADCAST' || tx.status === 'CANCELLED';
}

/** COB-461: how long a FINALIZED withdrawal must have sat before the Cancel
 *  button appears on it, in seconds.
 *
 *  A healthy FINALIZED row is broadcast by the watcher within one poll pass, so
 *  it is FINALIZED for seconds, not minutes. Ten minutes is comfortably past
 *  that and comfortably short of "the co-owners have given up". The gate exists
 *  so the button is not dangled over a withdrawal that is simply mid-flight —
 *  cancelling one of those would be a foot-gun, not a feature. */
export const FINALIZED_CANCEL_AGE_SECS = 600;

/** COB-461: may this row offer a Cancel button?
 *
 *  - COLLECTING → yes, always. Nothing has been assembled; calling it off is
 *    unambiguous and costs nothing.
 *  - FINALIZED  → only once it has been finalised for at least
 *    `FINALIZED_CANCEL_AGE_SECS` (and only if we actually know when that was —
 *    an absent `finalized_at_unix` means we cannot judge, so we do not offer).
 *    This is where cancel earns its keep: a FINALIZED row that can never be
 *    broadcast pins the vault out of EMPTY, and delete is gated on EMPTY.
 *  - BROADCAST / CANCELLED → no. Terminal.
 *
 *  **A display heuristic only.** The server applies no age gate — it authorises
 *  a cancel on membership, not on elapsed time — so this function only decides
 *  what to *show*. Never treat it as the security boundary. */
export function pendingTxCancellable(
  tx: SharedPendingTx,
  nowUnix: number,
): boolean {
  if (tx.status === 'COLLECTING') return true;
  if (tx.status !== 'FINALIZED') return false;
  if (tx.finalized_at_unix == null) return false;
  return nowUnix - tx.finalized_at_unix >= FINALIZED_CANCEL_AGE_SECS;
}

/** COB-461: the explanatory line under a CANCELLED row.
 *
 *  It deliberately does NOT name the canceller. The backend surfaces
 *  `cancelled_by_identity`, but that is a session identity pubkey, and the
 *  roster this page holds carries emails + slot ids — there is no identity
 *  field to join on, so any name shown here would be a guess. Date only
 *  (browser-local, via `formatDate`), and the sentence states the guarantee a
 *  cancel actually gives: we will not send it. */
export function pendingTxCancelledLabel(tx: SharedPendingTx): string {
  const tail = 'Cobault will not send this withdrawal.';
  return tx.cancelled_at_unix == null
    ? `Cancelled by a co-owner — ${tail}`
    : `Cancelled by a co-owner on ${formatDate(tx.cancelled_at_unix)} — ${tail}`;
}

// ── Per-party signer attribution (COB-362) ────────────────────────────

/** COB-362: one row of the per-party signing roster — a participant (or the
 *  appended verifier) with its status tag decided HERE, not in the render, so
 *  the label rules stay unit-testable. */
export interface SignerAttribution {
  /** Stable render key: the participant's slot_id, or `'verifier'`. */
  key: string;
  /** Display label — the participant's invited email, or "Cobault Verifier". */
  label: string;
  /** The status-tag text for this party. */
  tag: 'Signed' | 'Pending' | 'Did not sign' | 'Declined';
  /** The tag's tint modifier class, or `''` for the untinted base pill. */
  tagModifier: string;
}

/** COB-362: the per-party signing roster for a pending withdrawal — each human
 *  participant attributed from `signed_slots` (backend-authoritative: the
 *  slot_ids whose contributed key has a stored partial), with the verifier
 *  appended when the withdrawal opted in (`uses_verifier`). The verifier
 *  sentinel is never a participant slot, so it only ever appears via this
 *  appended row — never double-counted against a human slot.
 *
 *  Label rules (status-aware — "Pending" must never outlive the wait it
 *  describes):
 *    human    — signed → "Signed" (green); unsigned while the row is still
 *               COLLECTING → "Pending"; unsigned on a FINALIZED/BROADCAST row →
 *               "Did not sign" (untinted — quorum completed without them; a
 *               fact, not a wait state).
 *    verifier — `verifier_signed` → "Signed" (achieved-gold --ack); a REFUSED
 *               request → "Declined" (the --declined error tint, agreeing with
 *               the row's refusal detail line instead of contradicting it with
 *               "Pending"); otherwise Pending / Did not sign by the same
 *               COLLECTING gate as a human. */
export function pendingTxSignerAttribution(
  tx: SharedPendingTx,
  participants: SharedVaultParticipant[],
): SignerAttribution[] {
  const unsigned: Pick<SignerAttribution, 'tag' | 'tagModifier'> =
    tx.status === 'COLLECTING'
      ? { tag: 'Pending', tagModifier: '' }
      : { tag: 'Did not sign', tagModifier: '' };
  const rows: SignerAttribution[] = participants.map((p) => ({
    key: p.slot_id,
    // COB-543: a withdrawal only exists on an ACTIVE vault, so every slot here
    // is claimed and carries its holder's account email — but fall through the
    // same naming rules as the roster rather than assuming it.
    label: participantDisplayName(p).text,
    ...(tx.signed_slots.includes(p.slot_id)
      ? { tag: 'Signed' as const, tagModifier: 'shared-vault-tag--accepted' }
      : unsigned),
  }));
  if (tx.uses_verifier) {
    rows.push({
      key: 'verifier',
      label: 'Cobault Verifier',
      ...(tx.verifier_signed
        ? { tag: 'Signed' as const, tagModifier: 'shared-vault-tag--ack' }
        : tx.verifier_state === 'REFUSED'
          ? { tag: 'Declined' as const, tagModifier: 'shared-vault-tag--declined' }
          : unsigned),
    });
  }
  return rows;
}

// ── Verifier co-signing helpers (B2.1) ────────────────────────────────

/** Whether this vault can opt a withdrawal into verifier co-signing: it is a
 *  policy vault (the seal folded the additive verifier key `V` into the
 *  descriptor). On a no-policy vault the verifier has no key, so the option is
 *  hidden and `uses_verifier` is never sent.
 *
 *  COB-453 (SV-1): also requires the operator policy toggle to be on. While it
 *  is off the server 403s `uses_verifier`, so offering the switch would only
 *  produce a failed withdrawal; the option is hidden rather than greyed because
 *  on an already-sealed policy vault "coming soon" would be a lie — the
 *  capability exists, it is just currently withheld. Hiding it costs the user
 *  nothing: `m` humans finalise any withdrawal without the verifier (R-INV-1),
 *  which is exactly what the unchanged rest of this form does.
 *  `policyEnabled` defaults to `true` so a caller that has not plumbed the
 *  toggle (and the pre-SV-1 tests) sees the original behaviour. */
export function canEnableVerifier(
  vault: SharedVaultDetailResponse,
  policyEnabled = true,
): boolean {
  return vault.has_policy && policyEnabled;
}

/** The vault's configured rolling limit, read from the policy display mirror
 *  (A3.1 — COB-237). `null` for a no-policy vault, a policy vault with no limit,
 *  or when the mirror is absent.
 *
 *  This stays the **configured-limit mirror**: user-app shows the agreed ceiling
 *  and never computes or fetches how much of the window is still available.
 *  Live headroom against the verifier's own signed-amount ledger is shown on the
 *  verifier-shared review page ({@link VerifierReviewSection} links straight to
 *  it, COB-507) — the authority for the limit is, and remains, the verifier. */
export function configuredRollingLimit(
  vault: SharedVaultDetailResponse,
): SharedVaultRollingLimit | null {
  return vault.policy?.limit ?? null;
}

/**
 * COB-507: whether this vault has a verifier-shared contract for a participant
 * to review — the gate on {@link VerifierReviewSection}.
 *
 * Two conditions, both necessary:
 *
 *  - `has_policy` — the verifier-shared contract exists ONLY for a policy
 *    vault. A peer-to-peer vault never runs the verifier ceremony (no `V` is
 *    provisioned, no contract is registered), so there is nothing at
 *    `{verifier-shared}/v/{id}` to review and the link would 404.
 *  - the vault is **sealed** (`PROPOSED` / `ACTIVE` / `EMPTY`) — the contract
 *    is registered during the seal that moves `PENDING_INVITES → PROPOSED`
 *    (`seal_policy_vault`), so it does not exist before that. `REJECTED` and
 *    `DELETED` never sealed. This is the same state set
 *    `descriptorExportVisible` uses, and for the same reason: it is also the
 *    membership gate, since a pending invitee only ever sees a
 *    `PENDING_INVITES` vault.
 *
 * Deliberately NOT gated on `policyEnabled` (the SV-1 operator toggle): that
 * toggle governs whether the verifier will co-sign *new* withdrawals, not
 * whether the contract it holds may be read. Reviewing the agreed policy is
 * exactly as useful while co-signing is withheld.
 */
export function verifierReviewVisible(
  vault: SharedVaultDetailResponse,
): boolean {
  const sealed: SharedVaultState[] = ['PROPOSED', 'ACTIVE', 'EMPTY'];
  return vault.has_policy && sealed.includes(vault.state);
}

// ── Agreed-policy summary (COB-382) ───────────────────────────────────

/** COB-382: the agreed-policy summary the detail page renders for a vault. A
 *  *policy* vault (`has_policy`) carries the verifier allowlist + the configured
 *  authorisation-rate limit; a *peer-to-peer* vault has neither, so the section
 *  shows an explicit no-policy line rather than being hidden. Display mirror only
 *  (A3.1) — the verifier-shared contract is the source of truth; the SPA never
 *  enforces the policy, it only shows the agreed ceiling. */
export type PolicySummaryView =
  | { kind: 'p2p' }
  | {
      kind: 'policy';
      /** The verifier allowlist (display mirror, canonicalised server-side).
       *  Non-empty by construction — the backend refuses a limit-only policy —
       *  but defensively `[]` when the mirror is absent/null. */
      allowlist: string[];
      /** The configured authorisation-rate ceiling, or `null` for an
       *  allowlist-only policy (no rolling limit). */
      limit: SharedVaultRollingLimit | null;
    };

/** COB-382: decide whether the vault renders the policy card (allowlist + limit)
 *  or the peer-to-peer no-policy line. Keyed on `has_policy`; the allowlist and
 *  limit are read from the display mirror the same way the send / pending-row
 *  advisories read them. */
export function policySummaryView(
  vault: SharedVaultDetailResponse,
): PolicySummaryView {
  if (!vault.has_policy) return { kind: 'p2p' };
  return {
    kind: 'policy',
    allowlist: vault.policy?.allowlist ?? [],
    limit: configuredRollingLimit(vault),
  };
}

/** COB-382: the authorisation-rate ceiling as a display line, or `null` when the
 *  policy carries no limit. AR-11: the rolling limit is a ceiling on how much the
 *  *verifier* will co-sign per window — NOT a hard cap on what can leave the
 *  vault, since the human quorum can always withdraw without the verifier — so
 *  the copy attributes the rate to the verifier's co-signing, never a bare
 *  "spending limit". Reuses `formatRollingLimit` for the "X BTC per …" figure. */
export function policyLimitLine(
  limit: SharedVaultRollingLimit | null,
): string | null {
  if (!limit) return null;
  return `Verifier co-signs at most ${formatRollingLimit(limit)}`;
}

/** COB-382: the allowlist subsection heading with its entry count, e.g.
 *  "Verifier-allowlisted addresses (2)". */
export function allowlistHeading(count: number): string {
  return `Verifier-allowlisted addresses (${count})`;
}

/** The total co-signer count an activation bundle commits to: the `n` human
 *  roster keys plus the verifier key V for a policy vault (`m`-of-`(n+1)`), or
 *  just the human roster for a no-policy vault. The D2 modal shows `m of` this
 *  so the user sees the real signer set the descriptor folds V into. */
export function activationSignerCount(bundle: SharedVaultActivationBundle): number {
  return bundle.roster.length + (bundle.verifier_pubkey_hex ? 1 : 0);
}

/** COB-357: the R-INV-1 reassurance appended to a verifier-refusal line — the
 *  verifier is additive, so a policy decline never blocks the human quorum. */
const VERIFIER_QUORUM_REASSURANCE =
  'Participants can still complete this withdrawal with a full signature quorum.';

/** The verifier disposition of a pending withdrawal, as a small discriminated
 *  view driving both the status-zone tag and (when refused) the detail line
 *  (COB-357). A backend watcher auto-engages the verifier and persists the
 *  outcome; the SPA renders that truthfully — never hard-coded allowlist copy.
 *
 *    none     — the withdrawal did not opt into the verifier: no tag, no line.
 *    cosigns  — opted in, request still PENDING and unsigned, but the verifier
 *               is NOT yet the sole outstanding signature: the neutral
 *               "Verifier co-signs" tag (a fact about the signer set).
 *    awaiting — opted in, request PENDING, and the verifier is the one being
 *               awaited (COB-358 — the only outstanding signature is its own):
 *               an active "Verifier co-signing requested…" waiting state with a
 *               pulsing dot, so the row reads as "the watcher is engaging it",
 *               not the flat descriptor reading as "nothing is happening".
 *    signed   — the verifier's additive partial is stored: the achieved-gold
 *               "Verifier signed" tag (the `--ack` tint, a genuine achievement).
 *    declined — the verifier refused by policy (terminal): a short error-tinted
 *               "Verifier declined" tag plus a verbatim-reason detail line.
 *    off_allowlist — COB-362: the neutral `cosigns` arm superseded by an amber
 *               "Not on verifier allowlist" advisory when a destination is off
 *               the vault's allowlist (the verifier won't co-sign it), with a
 *               reassurance detail line. Computed from the display mirror — the
 *               verifier remains the authority; never supersedes a real event. */
export type PendingTxVerifierView =
  | { kind: 'none' }
  | { kind: 'cosigns'; tag: string; tagModifier: string }
  | { kind: 'awaiting'; tag: string; tagModifier: string }
  | { kind: 'signed'; tag: string; tagModifier: string }
  | { kind: 'declined'; tag: string; tagModifier: string; detail: string }
  | { kind: 'off_allowlist'; tag: string; tagModifier: string; detail: string };

/** Compose the refusal detail line: the verbatim verifier reason (or a bare
 *  "Verifier declined." when the backend omitted one) followed by the R-INV-1
 *  reassurance. The reason is rendered exactly as received — no allowlist copy,
 *  no mapping of status codes to canned strings. */
function verifierRefusalDetail(reason?: string): string {
  const head = reason ? `Verifier declined: ${reason}` : 'Verifier declined.';
  return `${head} ${VERIFIER_QUORUM_REASSURANCE}`;
}

/** COB-358: the verifier is the sole outstanding signature on this withdrawal —
 *  the row is still gathering partials (`COLLECTING`), it opted into the verifier
 *  (`uses_verifier`), the auto-engaged request has not yet resolved
 *  (`verifier_state === 'PENDING'`), and the collected partials are one short of
 *  the human threshold (`collected === quorum_m - 1`), so the verifier's additive
 *  partial is exactly what quorum is waiting on. The `COLLECTING` gate is
 *  load-bearing: it excludes a stale `BROADCAST`/`FINALIZED` row whose
 *  `verifier_state` never advanced past `PENDING` (a known staging row), so the
 *  waiting state never shows on a withdrawal that already went to the network.
 *  Keyed on the exact SPA field names (`verifier_state`, not a guessed
 *  `verifier_request_state`); `collected` counts humans only while the verifier
 *  is unsigned. */
export function pendingTxAwaitingVerifier(tx: SharedPendingTx): boolean {
  return (
    tx.status === 'COLLECTING' &&
    tx.uses_verifier &&
    tx.verifier_state === 'PENDING' &&
    tx.collected === tx.quorum_m - 1
  );
}

/** State-driven verifier disposition for a pending withdrawal (COB-357/358).
 *  Precedence: a stored partial (`verifier_signed` or state `SIGNED`) always
 *  reads as `signed`; a terminal `REFUSED` as `declined`; an opted-in PENDING
 *  withdrawal whose only outstanding signature is the verifier's reads as the
 *  active `awaiting` waiting state (COB-358); any other opted-in PENDING /
 *  transient-retrying withdrawal is the neutral `cosigns`. A withdrawal that
 *  never opted in is `none`. */
export function pendingTxVerifierView(tx: SharedPendingTx): PendingTxVerifierView {
  if (!tx.uses_verifier) return { kind: 'none' };
  if (tx.verifier_signed || tx.verifier_state === 'SIGNED') {
    return {
      kind: 'signed',
      tag: 'Verifier signed',
      tagModifier: 'shared-vault-tag--ack',
    };
  }
  if (tx.verifier_state === 'REFUSED') {
    return {
      kind: 'declined',
      tag: 'Verifier declined',
      tagModifier: 'shared-vault-tag--declined',
      detail: verifierRefusalDetail(tx.verifier_refusal_reason),
    };
  }
  if (pendingTxAwaitingVerifier(tx)) {
    return {
      kind: 'awaiting',
      tag: 'Verifier co-signing requested…',
      tagModifier: 'shared-vault-tag--awaiting',
    };
  }
  return {
    kind: 'cosigns',
    tag: 'Verifier co-signs',
    tagModifier: 'shared-vault-tag--cosigns',
  };
}

// ── Allowlist advisory (COB-362) ──────────────────────────────────────

/** Whether an address looks bech32/bech32m (hrp `bc1` / `tb1` / `bcrt1`,
 *  case-insensitive). Bech32 is canonically lowercase, so it is compared
 *  case-insensitively; base58 (everything else) is case-SENSITIVE — a case flip
 *  changes its checksum — so it is compared verbatim. */
function isBech32Like(address: string): boolean {
  return /^(bc1|tb1|bcrt1)/i.test(address.trim());
}

/** Normalise an address for allowlist membership: lowercase a bech32 address
 *  (the form the backend canonicalises the stored allowlist into), leave a
 *  base58 address verbatim. */
function normalizeAllowlistAddress(address: string): string {
  const trimmed = address.trim();
  return isBech32Like(trimmed) ? trimmed.toLowerCase() : trimmed;
}

/** COB-362: is `address` on the vault's verifier allowlist? The backend stores
 *  the allowlist CANONICALISED (bech32 lowercased) but a pending withdrawal's
 *  outputs VERBATIM as submitted, so both sides are normalised the same way
 *  before comparing (bech32 case-folded, base58 exact). */
export function addressOnAllowlist(address: string, allowlist: string[]): boolean {
  const norm = normalizeAllowlistAddress(address);
  return allowlist.some((a) => normalizeAllowlistAddress(a) === norm);
}

/** COB-362: a pending withdrawal is "off the verifier allowlist" when it opted
 *  into the verifier, the vault carries a non-empty policy allowlist, and at
 *  least one output address is not on it — the verifier refuses the WHOLE tx on
 *  any non-permitted output. Server-added change outputs never appear in
 *  `tx.outputs`, so they cannot false-trip this. A no-policy vault (or an absent
 *  allowlist mirror) is never off-allowlist. Advisory only: this is user-app's
 *  display mirror of the contract; the verifier remains the authority. */
export function pendingTxOffAllowlist(
  tx: SharedPendingTx,
  allowlist: string[] | null | undefined,
): boolean {
  if (!tx.uses_verifier) return false;
  if (!allowlist || allowlist.length === 0) return false;
  return tx.outputs.some((o) => !addressOnAllowlist(o.address, allowlist));
}

/** COB-362: the verifier disposition, with ONLY the neutral `cosigns` arm
 *  superseded by an amber "Not on verifier allowlist" warning when the
 *  withdrawal's outputs aren't all allowlisted. The awaiting / signed / declined
 *  arms reflect real verifier events and pass through untouched. The detail line
 *  keeps the copy advisory ("won't co-sign") + the R-INV-1 reassurance — never
 *  an absolute claim about the contract. */
export function pendingTxVerifierViewWithAllowlist(
  tx: SharedPendingTx,
  allowlist: string[] | null | undefined,
): PendingTxVerifierView {
  const view = pendingTxVerifierView(tx);
  if (view.kind === 'cosigns' && pendingTxOffAllowlist(tx, allowlist)) {
    return {
      kind: 'off_allowlist',
      tag: 'Not on verifier allowlist',
      tagModifier: 'shared-vault-tag--warn',
      detail: `This destination isn't on the vault's verifier allowlist, so the verifier won't co-sign it. ${VERIFIER_QUORUM_REASSURANCE}`,
    };
  }
  return view;
}

/** COB-362: the creation-time advisory — with verifier co-signing ON, warn
 *  (non-blocking) when a non-empty recipient address is off the vault's
 *  allowlist. Empty rows and no-policy / no-verifier states never warn; it never
 *  gates submission (`canInitiateWithdrawal` is unchanged). */
export function sendRowOffAllowlist(
  row: SendRow,
  useVerifier: boolean,
  allowlist: string[] | null | undefined,
): boolean {
  if (!useVerifier) return false;
  if (!allowlist || allowlist.length === 0) return false;
  if (row.address.trim() === '') return false;
  return !addressOnAllowlist(row.address, allowlist);
}

/** The vault is delete-eligible (EMPTY, and the server agrees via can_delete). */
export function canDeleteSharedVault(vault: SharedVaultDetailResponse): boolean {
  return vault.can_delete && vault.state === 'EMPTY';
}

// COB-355: balancePanelModel / awaitingConfirmationHint (COB-354) moved to
// src/balance.ts so the wallet can share the same spendable-first model;
// the panel itself now renders through <BalancePanel>.

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ── Page ───────────────────────────────────────────────────────────────

/** Phase-1 shared-vault detail. Read-only foundation (Slice 0) + the B4
 *  interactive controls (PENDING_INVITES management, PROPOSED → ACTIVE
 *  acknowledgement) + the B5 active-vault flow (COB-205): balance, multisig
 *  send (D3 intent), per-withdrawal co-signer Sign (D3/D4 clear-sign), and the
 *  B6 delete gate. */
/** Max display-name length — mirrors the backend validation + DB CHECK. */
export const SHARED_VAULT_NAME_MAX = 60;

/** The vault's effective display name, or `null` when unnamed / blank — the
 *  header then falls back to the quorum descriptor as its title. */
export function sharedVaultDisplayName(
  vault: Pick<SharedVaultDetailResponse, 'name'>,
): string | null {
  const trimmed = vault.name?.trim();
  return trimmed ? trimmed : null;
}

/** The quorum descriptor line, e.g. "2-of-4 shared vault". It is the header
 *  title when the vault is unnamed, and the demoted muted suffix when it is
 *  named. */
export function sharedVaultQuorumDescriptor(
  vault: Pick<SharedVaultDetailResponse, 'quorum_m' | 'n' | 'has_policy'>,
): string {
  return `${quorumLabel(vault.quorum_m, totalKeys(vault))} shared vault`;
}

/** Detail-page header. When the vault is named, the human name is the title and
 *  the quorum descriptor ("2-of-4 shared vault") is demoted to a muted suffix
 *  line; when unnamed, the header is unchanged (quorum descriptor as title). A
 *  small ghost "✎" affordance (any participant may rename — one shared name per
 *  vault) swaps in an inline input on click. Self-contained edit state so the
 *  parent only supplies the persist callback. */
export function SharedVaultDetailHead({
  vault,
  onRename,
}: {
  vault: SharedVaultDetailResponse;
  onRename: (name: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const name = sharedVaultDisplayName(vault);
  const quorum = sharedVaultQuorumDescriptor(vault);
  const tone = sharedVaultStateTone(vault.state);

  function startEditing() {
    setDraft(name ?? '');
    setError(null);
    setEditing(true);
  }

  function cancel() {
    if (saving) return;
    setEditing(false);
    setError(null);
  }

  async function save() {
    const trimmed = draft.trim();
    if (trimmed === '' || trimmed.length > SHARED_VAULT_NAME_MAX) {
      setError(`Enter a name of 1 to ${SHARED_VAULT_NAME_MAX} characters.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onRename(trimmed);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not rename the vault.');
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div class="shared-vault-detail-head">
        <div class="shared-vault-rename">
          <input
            class="accept-input shared-vault-rename-input"
            type="text"
            aria-label="Vault name"
            maxLength={SHARED_VAULT_NAME_MAX}
            value={draft}
            disabled={saving}
            onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          />
          <button
            type="button"
            class="btn btn-primary"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            class="btn btn-secondary"
            onClick={cancel}
            disabled={saving}
          >
            Cancel
          </button>
          {error && <p class="vault-card-error">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div class="shared-vault-detail-head">
      {name ? (
        <div class="shared-vault-title-group">
          <h1 class="vaults-page-title">{name}</h1>
          <p class="shared-vault-detail-quorum">{quorum}</p>
        </div>
      ) : (
        <h1 class="vaults-page-title">{quorum}</h1>
      )}
      <button
        type="button"
        class="btn btn-link shared-vault-rename-btn"
        aria-label="Rename vault"
        onClick={startEditing}
      >
        ✎
      </button>
      <span class={`shared-vault-badge shared-vault-badge--${tone}`}>
        {sharedVaultStateLabel(vault.state)}
      </span>
    </div>
  );
}

export function SharedVaultDetail() {
  const { params } = useRoute();
  const { route } = useLocation();
  const session = useSession();
  const [vault, setVault] = useState<SharedVaultDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acking, setAcking] = useState(false);
  // Signing-checkpoint modal (COB-218): re-derive + review the sealed contract
  // before the contributed key signs the activation manifest.
  const [ackModalOpen, setAckModalOpen] = useState(false);
  const [ackBundle, setAckBundle] = useState<SharedVaultActivationBundle | null>(null);
  const [ackBundleError, setAckBundleError] = useState<string | null>(null);
  const [busySlot, setBusySlot] = useState<string | null>(null);
  const [busyTx, setBusyTx] = useState<string | null>(null);
  // COB-350: per-row "Review & sign" outcome (success or failure), keyed by the
  // pending-tx id. handleSign's result was previously surfaced only in the
  // top-of-page banner — two screens above the button — so a signed or failed
  // click looked like a no-op (and a partial recorded on an m≥2 vault leaves
  // the row visibly at COLLECTING). Rendered inline beside each row's button.
  const [signRowMsg, setSignRowMsg] = useState<{
    id: string;
    kind: 'error' | 'success';
    text: string;
  } | null>(null);
  // COB-461: per-row Cancel outcome, kept separate from `signRowMsg` so the two
  // never overwrite one another and so this one is NOT poll-cleared —
  // `signBannerResolvedByPoll` retires a sign banner once the row leaves
  // COLLECTING, which a cancel does immediately, and that would blank the
  // message in the same tick it was set.
  const [cancelRowMsg, setCancelRowMsg] = useState<{
    id: string;
    kind: 'error' | 'success';
    text: string;
  } | null>(null);
  // COB-352 (symptom 3): the Create-withdrawal outcome, rendered inline beside
  // the Create button. handleSend previously surfaced failures only in the
  // top-of-page banner — several sections above the Send form on a policy vault
  // — and the validation guard returned silently, so a failed or invalid create
  // read as "the button does nothing". Same inline-adjacent convention as
  // COB-350's per-row signRowMsg.
  const [sendMsg, setSendMsg] = useState<{
    kind: 'error' | 'success';
    text: string;
    // COB-362: for a success banner, the created withdrawal's id — the poll
    // clears the banner once that row is visible (sendBannerResolvedByPoll).
    id?: string;
  } | null>(null);
  // COB-543: claim links minted by a regenerate this session, keyed by slot_id.
  // The server keeps only their SHA-256, so this map is the sole copy of each
  // raw link — it is deliberately never re-fetched, never persisted, and gone on
  // reload. A slot appears here only while its freshly-minted link is on screen.
  const [issuedLinks, setIssuedLinks] = useState<Record<string, IssuedInvite>>(
    {},
  );
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sendRows, setSendRows] = useState<SendRow[]>([blankSendRow()]);
  // COB-485: the fee control. `feeSelection` is which option (or custom value)
  // is chosen; `feeLadder` is the SERVED ladder it resolves against — the
  // browser never invents a rate. Replaces the free-text box that defaulted
  // to '1' and caused the 2026-08-05 stuck withdrawal.
  const [feeSelection, setFeeSelection] = useState<FeeSelection>(
    initialFeeSelection(),
  );
  const [feeLadder, setFeeLadder] = useState<FeeLadderView | null>(null);
  // B2.1: opt this withdrawal into verifier co-signing (policy vaults only).
  // Defaults OFF — the verifier is an explicit, additive opt-in per withdrawal.
  const [useVerifier, setUseVerifier] = useState(false);
  // COB-453 (SV-1): the operator policy toggle. While it is off the server 403s
  // `uses_verifier`, so `canEnableVerifier` folds this in and the opt-in switch
  // is not offered. `true` (the server default) leaves this page unchanged.
  const policyEnabled = useSharedVaultPolicyEnabled();
  // COB-362: hide terminal (BROADCAST) withdrawals by default so the list stays
  // scannable; a header toggle reveals them. Defaults OFF (completed hidden).
  const [showCompleted, setShowCompleted] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  // COB-499: the spec download. Lazy — the sealed activation bundle is fetched
  // on the click, never on render, so a page view costs nothing extra.
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const fetchVault = useCallback(() => {
    if (!params.id) return;
    if (!session.isAuthenticated) return;
    api
      .getSharedVault(params.id)
      .then((v) => {
        setVault(v);
        setError(null);
        // COB-362: reconcile the ephemeral success banners against fresh state
        // so a frozen "N of M" / "waiting for co-signers" acknowledgement never
        // outlives the row it described. Error banners are left untouched.
        setSignRowMsg((prev) =>
          signBannerResolvedByPoll(prev, v.pending_tx) ? null : prev,
        );
        setSendMsg((prev) =>
          sendBannerResolvedByPoll(prev, v.pending_tx) ? null : prev,
        );
      })
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Failed to load vault'),
      );
  }, [params.id, session.isAuthenticated]);

  useEffect(() => {
    fetchVault();
  }, [fetchVault]);

  // Auto-refresh while the vault is still in a non-terminal state so an
  // accept / acknowledge / on-chain / co-signer transition by another party
  // surfaces without a manual reload.
  useEffect(() => {
    if (!vault) return;
    if (TERMINAL_STATES.has(vault.state)) return;
    const interval = setInterval(fetchVault, 5000);
    return () => clearInterval(interval);
  }, [vault?.state, fetchVault]);

  // ── COB-485: the served fee ladder ────────────────────────────────────
  //
  // Two shapes, one state slot. With at least one usable output row we ask
  // the withdrawal-quote endpoint, which prices every tier for *this* draft
  // (`est_vsize` + per-tier `fee_sats` from the builder's own code). With no
  // rows yet — or if the quote can't be served, e.g. the vault's funds are
  // still confirming — we fall back to the bare `/fee-tiers` ladder, so the
  // dropdown still shows real rates, just without totals.
  const sendOutputsKey = JSON.stringify(buildSendOutputs(sendRows));
  const refreshFeeLadder = useCallback(async (): Promise<FeeLadderView | null> => {
    if (!params.id) return null;
    if (!session.isAuthenticated) return null;
    const outputs: SharedWithdrawalOutput[] = JSON.parse(sendOutputsKey);
    if (outputs.length > 0) {
      try {
        const quote = await api.quoteSharedWithdrawal(params.id, { outputs });
        setFeeLadder(quote);
        return quote;
      } catch {
        // Fall through to the un-priced ladder below.
      }
    }
    try {
      const tiers = await api.getFeeTiers();
      setFeeLadder(tiers);
      return tiers;
    } catch {
      // Leave whatever we last had; the selector renders its own
      // "fetching rates" state when it has nothing at all.
      return null;
    }
  }, [params.id, session.isAuthenticated, sendOutputsKey]);

  // Fetch on mount and whenever the output set changes, debounced so typing
  // an address does not fan out a request per keystroke.
  useEffect(() => {
    if (vault?.state !== 'ACTIVE') return;
    const t = setTimeout(() => {
      void refreshFeeLadder();
    }, FEE_LADDER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [vault?.state, refreshFeeLadder]);

  // …then keep it fresh while the form is open: every 60s and on window
  // focus (a user who tabbed away for an hour must not submit an hour-old
  // rate). The pre-submit re-price in `handleSend` is the last line.
  useEffect(() => {
    if (vault?.state !== 'ACTIVE') return;
    const onFocus = () => void refreshFeeLadder();
    const interval = setInterval(onFocus, FEE_LADDER_REFRESH_MS);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [vault?.state, refreshFeeLadder]);

  const feeError = feeSelectionError(feeSelection, feeLadder);

  function resetBanners() {
    setActionError(null);
    setActionNotice(null);
  }

  /** D2 (signing checkpoint): open the review modal and fetch the sealed
   *  activation bundle so the participant can see the exact deposit address +
   *  contract their key is about to attest to before signing. */
  async function openSigningCheckpoint() {
    if (!vault) return;
    resetBanners();
    setAckBundle(null);
    setAckBundleError(null);
    setAckModalOpen(true);
    try {
      setAckBundle(await api.getSharedVaultActivation(vault.vault_id));
    } catch (e) {
      setAckBundleError(
        e instanceof Error ? e.message : 'Could not load the vault contract.',
      );
    }
  }

  function closeSigningCheckpoint() {
    if (acking) return; // never dismiss mid-sign
    setAckModalOpen(false);
    setAckBundle(null);
    setAckBundleError(null);
  }

  /** D2: sign the activation manifest with the contributed multisig key over the
   *  exact bundle the participant just reviewed (the agent re-derives + verifies
   *  it before signing — R-INV-5), then submit the acknowledgement. */
  async function handleAcknowledge(bundle: SharedVaultActivationBundle) {
    if (!vault) return;
    if (!session.agent) {
      setActionError('Your session is locked. Sign in again to review & sign.');
      return;
    }
    setAcking(true);
    try {
      const proof = await session.agent.acknowledgeActivation(
        vault.vault_id,
        bundle,
        vault.my_derivation_index,
      );
      // WS-H v2 proof (HW.8): `der-sha256-v1` (software) / `bsm-bip137-v1` (device).
      await api.acknowledgeSharedVault(vault.vault_id, {
        activation_manifest_proof: proof,
      });
      setAckModalOpen(false);
      setAckBundle(null);
      fetchVault();
    } catch (e) {
      // Keep the modal open so the error is shown in context (e.g. the
      // account-mismatch message tells the user to switch sign-in).
      setAckBundleError(
        e instanceof Error ? e.message : 'Could not sign to activate the vault.',
      );
    } finally {
      setAcking(false);
    }
  }

  /** B5: initiate a withdrawal — sign the AR-14 intent over the chosen output
   *  set with this vault's multisig key (D3), then POST it. */
  async function handleSend() {
    if (!vault) return;
    if (!session.agent) {
      setSendMsg({
        kind: 'error',
        text: 'Your session is locked. Sign in again to send.',
      });
      return;
    }
    resetBanners();
    setSendMsg(null);
    const outputs = buildSendOutputs(sendRows);
    if (outputs.length === 0 || feeError !== null) {
      // Was a silent `return` — the button looked like a no-op. Name the gap.
      setSendMsg({ kind: 'error', text: sendValidationMessage(sendRows, feeError) });
      return;
    }
    setSending(true);
    try {
      // COB-485 §2.4: re-price immediately before submitting. Quorum
      // collection is human-paced and the form may have sat open for a while,
      // so the number on the control can be stale by now. If the chosen tier's
      // rate has moved more than 20%, show the new figure and require one
      // explicit re-confirm rather than silently building at a rate the user
      // never saw. A custom rate is unaffected — it is not derived from the
      // ladder, so it cannot move under the user.
      const rateAtRender = selectedFeeRate(feeSelection, feeLadder);
      const fresh = await refreshFeeLadder();
      const ladderNow = fresh ?? feeLadder;
      const feeRate = selectedFeeRate(feeSelection, ladderNow);
      // Both early returns land in the `finally` below, which clears
      // `sending` — the button never sticks on "Creating…".
      if (feeRate === null) {
        setSendMsg({
          kind: 'error',
          text: 'Could not read the current network fee rates. Try again.',
        });
        return;
      }
      if (rateAtRender !== null && rateMovedMaterially(rateAtRender, feeRate)) {
        // The re-confirm: `refreshFeeLadder` has already stored the new
        // ladder, so the control now shows the new number and a second click
        // compares it against itself and proceeds.
        setSendMsg({
          kind: 'error',
          text: rateMovedNotice(rateAtRender, feeRate),
        });
        return;
      }
      // COB-350: the agent resolves its signing key against this bundle's
      // roster before minting the intent (create-time roster guard) — a
      // wrong-seed session throws the agent's actionable message here instead
      // of minting a withdrawal no co-signer could ever verify.
      const bundle = await api.getSharedVaultActivation(vault.vault_id);
      const intentProof = await session.agent.signSharedWithdrawalIntent(
        vault.vault_id,
        bundle,
        outputs,
        vault.my_derivation_index,
      );
      // Only a policy vault may opt in (the backend rejects uses_verifier on a
      // no-policy vault); never claim the verifier on a vault that has no key.
      const wantsVerifier = canEnableVerifier(vault, policyEnabled) && useVerifier;
      // WS-H v2 proof (HW.8): `der-sha256-v1` (software) / `bsm-bip137-v1` (device).
      const created = await api.initiateSharedWithdrawal(vault.vault_id, {
        outputs,
        fee_rate: feeRate,
        // COB-485: observability only — the server logs the tier alongside
        // the rate. `fee_rate` remains the sole authority.
        fee_tier: feeTierForRequest(feeSelection),
        uses_verifier: wantsVerifier,
        intent_proof: intentProof,
      });
      // COB-356: verifier co-signing is no longer a manual per-row action (the
      // request button is retired; a backend watcher engages it), so the success
      // copy no longer instructs the user to request it.
      // COB-362: this is a transient acknowledgement — the poll clears it once
      // the created row is visible (sendBannerResolvedByPoll), so a stale
      // "waiting for co-signers" line never lingers over an advanced row.
      setSendMsg({
        kind: 'success',
        id: created.pending_tx_id,
        text: 'Withdrawal created — waiting for co-signers to sign.',
      });
      // Reset the form only on success (a failed create keeps the inputs).
      // COB-485: back to the default tier, not to a hardcoded '1' sat/vB.
      setSendRows([blankSendRow()]);
      setFeeSelection(initialFeeSelection());
      fetchVault();
    } catch (e) {
      setSendMsg({
        kind: 'error',
        text: e instanceof Error ? e.message : 'Could not create the withdrawal.',
      });
    } finally {
      setSending(false);
    }
  }

  /** B5: co-sign a pending withdrawal — fetch the unsigned PSBT + the sealed
   *  roster, run the D4 multi-party clear-sign locally (WYSIWYS + AR-3 + verify
   *  the initiator's intent), add this signer's partial (D3), and submit. */
  async function handleSign(tx: SharedPendingTx) {
    if (!vault) return;
    if (!session.agent) {
      setSignRowMsg({
        id: tx.pending_tx_id,
        kind: 'error',
        text: 'Your session is locked. Sign in again to sign.',
      });
      return;
    }
    resetBanners();
    setSignRowMsg(null);
    setBusyTx(tx.pending_tx_id);
    try {
      const status = await api.getSharedWithdrawalStatus(
        vault.vault_id,
        tx.pending_tx_id,
      );
      const bundle = await api.getSharedVaultActivation(vault.vault_id);
      const partial = await session.agent.partialSignSharedWithdrawal(
        vault.vault_id,
        {
          psbt: hexToBytes(status.unsigned_psbt_hex),
          quorumM: bundle.quorum_m,
          roster: bundle.roster,
          // Policy vault: V is folded into the AR-3 vault-UTXO re-derivation
          // (the sealed descriptor is over `roster ∪ {V}`). Null for no-policy.
          verifierPubkeyHex: bundle.verifier_pubkey_hex,
          outputs: status.outputs,
          // The status surfaces exactly one intent credential form (WS-H §6.6);
          // pass both through — the co-signer's clear-sign dispatches on which
          // is present and verifies it against the roster (dual-format, HW.8).
          intentSig: status.intent_sig,
          intentProof: status.intent_proof,
        },
        vault.my_derivation_index,
      );
      const res = await api.submitSharedPartialSig(
        vault.vault_id,
        tx.pending_tx_id,
        { partial_psbt_hex: bytesToHex(partial) },
      );
      // COB-362: a count-free acknowledgement — the live "N of M signatures"
      // count lives in the state-driven signed line, kept current by the poll
      // (which also clears this banner once the row leaves COLLECTING). Freezing
      // a count here left "1 of 2 signatures…" lingering over a FINALIZED badge.
      setSignRowMsg({
        id: tx.pending_tx_id,
        kind: 'success',
        text: signSuccessMessage(res.state),
      });
      fetchVault();
    } catch (e) {
      setSignRowMsg({
        id: tx.pending_tx_id,
        kind: 'error',
        text: e instanceof Error ? e.message : 'Could not sign the withdrawal.',
      });
    } finally {
      setBusyTx(null);
    }
  }

  /** COB-461: call off a pending withdrawal. Any member may — the server
   *  authorises on membership, not on who initiated it.
   *
   *  No client-side confirm step, matching `handleDelete` (the repo's other
   *  irreversible member action): the destination + amount are already on the
   *  row the button sits in, and the outcome is announced inline. A cancel is
   *  also the *safe* direction — it never moves funds; the reversal is simply
   *  creating the withdrawal again. */
  async function handleCancel(tx: SharedPendingTx) {
    if (!vault) return;
    resetBanners();
    setCancelRowMsg(null);
    setBusyTx(tx.pending_tx_id);
    try {
      await api.cancelSharedWithdrawal(vault.vault_id, tx.pending_tx_id);
      setCancelRowMsg({
        id: tx.pending_tx_id,
        kind: 'success',
        text: 'Withdrawal cancelled — it will not be sent.',
      });
      fetchVault();
    } catch (e) {
      setCancelRowMsg({
        id: tx.pending_tx_id,
        kind: 'error',
        text:
          e instanceof Error ? e.message : 'Could not cancel the withdrawal.',
      });
    } finally {
      setBusyTx(null);
    }
  }

  /** B6: soft-delete a drained (EMPTY) vault. */
  async function handleDelete() {
    if (!vault) return;
    resetBanners();
    setDeleting(true);
    try {
      await api.deleteSharedVault(vault.vault_id);
      route('/shared-vaults');
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : 'Could not delete the vault.',
      );
      setDeleting(false);
    }
  }

  /** COB-499: fetch the sealed activation bundle, render the vault spec, and
   *  hand it to the browser as a download. The bundle is the same one the
   *  signing checkpoint reads; nothing is cached between clicks so the file
   *  always reflects the current sealed record. */
  async function handleDescriptorExport() {
    if (!vault) return;
    setExportError(null);
    setExporting(true);
    try {
      const bundle = await api.getSharedVaultActivation(vault.vault_id);
      downloadTextFile(
        buildSharedVaultSpec(vault, bundle),
        sharedVaultSpecFilename(vault.vault_id),
        'application/json',
      );
    } catch (e) {
      setExportError(
        e instanceof Error ? e.message : 'Could not build the vault spec.',
      );
    } finally {
      setExporting(false);
    }
  }

  /** Persist a rename (any accepted participant may do this — one shared name
   *  per vault). Echoes the trimmed name into local state for an immediate
   *  update, then reconciles via the same refetch the other mutations use. Any
   *  API error propagates so the header can show it inline. */
  async function handleRename(name: string) {
    if (!vault) return;
    resetBanners();
    const res = await api.renameSharedVault(vault.vault_id, name);
    setVault((prev) => (prev ? { ...prev, name: res.name } : prev));
    fetchVault();
  }

  /** COB-543: mint a fresh claim link for a still-unclaimed slot. This is a
   *  rotation, not a re-send — the slot's previous token dies the moment the new
   *  one is issued (AR-17), so the old link stops working even if it was never
   *  lost. The returned URL is the ONLY time the raw token is legible, so it is
   *  held in local state and shown inline on that row; nothing re-fetches it. */
  async function handleRegenerate(slotId: string) {
    if (!vault) return;
    resetBanners();
    setIssuedLinks((cur) => {
      const next = { ...cur };
      delete next[slotId];
      return next;
    });
    setBusySlot(slotId);
    try {
      const issued = await api.regenerateSharedVaultInvite(vault.vault_id, slotId);
      setIssuedLinks((cur) => ({ ...cur, [slotId]: issued }));
      setActionNotice(
        'New claim link generated — the previous link no longer works.',
      );
      fetchVault();
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : 'Could not generate a new claim link.',
      );
    } finally {
      setBusySlot(null);
    }
  }

  async function handleRemove(slotId: string) {
    if (!vault) return;
    resetBanners();
    setBusySlot(slotId);
    try {
      await api.deleteSharedVaultInvite(vault.vault_id, slotId);
      setActionNotice('Invitation removed.');
      fetchVault();
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : 'Could not remove the invitation.',
      );
    } finally {
      setBusySlot(null);
    }
  }

  if (!session.isAuthenticated) {
    return (
      <div class="vault-detail-page">
        <p class="vault-list-empty">Log in to view this shared vault.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div class="vault-detail-page">
        <a class="shared-vault-back" href="/shared-vaults">
          ← Shared vaults
        </a>
        <p class="vault-card-error">{error}</p>
      </div>
    );
  }

  if (!vault) {
    return (
      <div class="vault-detail-page">
        <p class="vault-list-empty">Loading…</p>
      </div>
    );
  }

  const manageInvites =
    vault.state === 'PENDING_INVITES' && isInitiator(vault.participants);

  // COB-354: lead the balance panel with the spendable (confirmed) figure and,
  // separately, the still-confirming surplus — so the headline never advertises
  // funds the confirmed-only withdrawal builder cannot yet spend.
  const balanceModel = balancePanelModel(vault);

  return (
    <div class="vault-detail-page">
      <a class="shared-vault-back" href="/shared-vaults">
        ← Shared vaults
      </a>
      <SharedVaultDetailHead vault={vault} onRename={handleRename} />
      <p class="shared-vault-detail-id mono">{vault.vault_id}</p>

      {actionError && <p class="vault-card-error">{actionError}</p>}
      {actionNotice && <p class="shared-vault-notice">{actionNotice}</p>}

      {activationFinishing(vault) && (
        <p class="shared-vault-notice">
          Every participant has consented — the vault is finishing activation.
          This completes automatically.
        </p>
      )}

      {vault.state === 'PROPOSED' && (
        <ActivationSection vault={vault} onReview={openSigningCheckpoint} />
      )}

      {ackModalOpen && vault && (
        <ActivationSigningModal
          bundle={ackBundle}
          error={ackBundleError}
          signing={acking}
          onConfirm={handleAcknowledge}
          onCancel={closeSigningCheckpoint}
        />
      )}

      {depositAddressVisible(vault) && vault.deposit_address && (
        <section class="shared-vault-section">
          <h2 class="shared-vault-section-title">Deposit / receive address</h2>
          <DepositAddressField address={vault.deposit_address} label="" />
          <BalancePanel
            label="Balance"
            headlineSats={balanceModel.headlineSats}
            pendingSats={balanceModel.pendingSats}
          />
        </section>
      )}

      {/* COB-486: pre-activation the section keeps its place (so the address
          does not appear to arrive from nowhere on activation) but carries a
          hint instead of an address. Only while the vault is still on its way
          to ACTIVE — a REJECTED or DELETED vault gets no section at all. */}
      {!depositAddressVisible(vault) &&
        (vault.state === 'PENDING_INVITES' || vault.state === 'PROPOSED') && (
          <section class="shared-vault-section">
            <h2 class="shared-vault-section-title">Deposit / receive address</h2>
            <p class="accept-input-hint">
              The deposit address becomes available once all participants have
              signed.
            </p>
          </section>
        )}

      <PolicySummarySection vault={vault} />

      {/* COB-507: the arm's-length copy of the same policy, on the verifier's
          own site. Rendered only when there is a contract to review (policy
          vault, sealed) AND the vault's network has a mapped verifier-shared
          origin — an unmapped network suppresses the link rather than pointing
          a participant at a wrong host. */}
      {verifierReviewVisible(vault) &&
        (() => {
          const reviewUrl = verifierSharedVaultUrlForNetwork(
            vault.network,
            vault.vault_id,
          );
          return reviewUrl ? <VerifierReviewSection reviewUrl={reviewUrl} /> : null;
        })()}

      {vault.state === 'ACTIVE' && (
        <SendSection
          rows={sendRows}
          quorumM={vault.quorum_m}
          n={vault.n}
          feeLadder={feeLadder}
          feeSelection={feeSelection}
          sending={sending}
          canSend={canInitiateWithdrawal(vault.state, sendRows, feeError)}
          awaitingHint={awaitingConfirmationHint(vault)}
          showVerifierOption={canEnableVerifier(vault, policyEnabled)}
          allowlist={vault.policy?.allowlist ?? null}
          useVerifier={useVerifier}
          onUseVerifierChange={setUseVerifier}
          onRowsChange={setSendRows}
          onFeeSelectionChange={setFeeSelection}
          onSend={handleSend}
          msg={sendMsg}
        />
      )}

      <ParticipantsSection
        vault={vault}
        manageInvites={manageInvites}
        busySlot={busySlot}
        issuedLinks={issuedLinks}
        onRegenerate={handleRegenerate}
        onDismissLink={(slotId) =>
          setIssuedLinks((cur) => {
            const next = { ...cur };
            delete next[slotId];
            return next;
          })
        }
        onRemove={handleRemove}
      />

      {vault.pending_tx.length > 0 &&
        (() => {
          // COB-362: hide terminal rows by default; the toggle (shown only when
          // there is something to reveal) flips the whole set. The section stays
          // visible even when every row is terminal so the toggle remains
          // reachable rather than the list vanishing.
          //
          // COB-461: terminal now also means CANCELLED, so the copy says
          // "closed", not "completed" — a cancelled withdrawal was not
          // completed, and labelling it so would misreport what happened.
          const terminalCount = vault.pending_tx.filter(pendingTxIsTerminal).length;
          // Recomputed each render (the page polls), so a FINALIZED row that
          // ages past the cancel threshold picks up its button on a later pass.
          const nowUnix = Math.floor(Date.now() / 1000);
          const visiblePending = showCompleted
            ? vault.pending_tx
            : vault.pending_tx.filter((tx) => !pendingTxIsTerminal(tx));
          return (
            <section class="shared-vault-section">
              <div class="shared-vault-section-header">
                <h2 class="shared-vault-section-title">Withdrawals</h2>
                {terminalCount > 0 && (
                  <button
                    type="button"
                    class="shared-vault-section-filter"
                    onClick={() => setShowCompleted((v) => !v)}
                  >
                    {showCompleted
                      ? 'Hide closed'
                      : `Show all (${terminalCount} closed)`}
                  </button>
                )}
              </div>
              <ul class="shared-vault-pending-list">
                {visiblePending.map((tx) => (
                  <PendingTxRow
                    key={tx.pending_tx_id}
                    tx={tx}
                    participants={vault.participants}
                    allowlist={vault.policy?.allowlist ?? null}
                    feeLadder={feeLadder}
                    busy={busyTx === tx.pending_tx_id}
                    signMsg={
                      signRowMsg?.id === tx.pending_tx_id ? signRowMsg : null
                    }
                    cancelMsg={
                      cancelRowMsg?.id === tx.pending_tx_id ? cancelRowMsg : null
                    }
                    canCancel={pendingTxCancellable(tx, nowUnix)}
                    onSign={() => handleSign(tx)}
                    onCancel={() => handleCancel(tx)}
                  />
                ))}
              </ul>
              {visiblePending.length === 0 && (
                <p class="accept-input-hint">
                  No withdrawals are in progress. Use “Show all” to view the
                  closed ones.
                </p>
              )}
            </section>
          );
        })()}

      {depositAddressVisible(vault) && (
        <SharedVaultTransactions vaultId={vault.vault_id} />
      )}

      {/* COB-499. The state gate is also the membership gate this section
          needs: it admits only PROPOSED/ACTIVE/EMPTY, and a pending invitee
          only ever sees a PENDING_INVITES vault. */}
      {descriptorExportVisible(vault) && (
        <DescriptorExportSection
          exporting={exporting}
          error={exportError}
          onDownload={() => void handleDescriptorExport()}
        />
      )}

      {canDeleteSharedVault(vault) && (
        <section class="shared-vault-section">
          <h2 class="shared-vault-section-title">Delete vault</h2>
          <p class="accept-input-hint">
            This vault is empty. Deleting removes it from your list; it cannot be
            undone.
          </p>
          <button
            type="button"
            class="btn btn-danger"
            disabled={deleting}
            onClick={handleDelete}
          >
            {deleting ? 'Deleting…' : 'Delete vault'}
          </button>
        </section>
      )}
    </div>
  );
}

// COB-543: the pending-invitee section (COB-221 / COB-337) is gone. It existed
// so a caller whose slot named their email could consent from the vault page
// without a token. A slot names nobody now, and a caller who has not claimed one
// is not a member — the backend does not admit them to this page at all. The
// claim link is the only way in, and the claim page is where it lands.

// ── Activation / signing checkpoint (PROPOSED → ACTIVE, D2) ────────────

function ActivationSection({
  vault,
  onReview,
}: {
  vault: SharedVaultDetailResponse;
  onReview: () => void;
}) {
  const acked = acknowledgedCount(vault.participants);
  const mine = canAcknowledge(vault);
  return (
    <section class="shared-vault-section">
      <h2 class="shared-vault-section-title">Signing checkpoint</h2>
      <p class="shared-vault-activation-progress tabular-nums">
        {activationProgressLabel(acked, vault.n)}
      </p>
      {mine ? (
        <>
          <p class="accept-input-hint">
            Now that every participant has consented, review this vault's
            deposit contract — re-derived in your browser from the full roster —
            and sign to activate the vault. Your signature is over this vault's
            own setup: its deposit contract, roster and quorum. The vault can
            receive deposits once every participant has signed.{' '}
            {ROSTER_VERIFICATION_NOTE}
          </p>
          <button type="button" class="btn btn-primary" onClick={onReview}>
            Review &amp; sign
          </button>
        </>
      ) : (
        <p class="accept-input-hint">
          Waiting for every participant to sign and activate the vault before it
          goes live. {ROSTER_VERIFICATION_NOTE}
        </p>
      )}
    </section>
  );
}

/** COB-543 (D7): the roster check every signer owes themselves.
 *
 *  A slot is claimed by whoever holds its link, so the roster is the only place
 *  the outcome of that distribution becomes visible — and the activation
 *  signature is the last moment before the vault can hold bitcoin. If a link
 *  went astray, the wrong account is sitting on the roster right now, and the
 *  only party who can catch it is a participant who knows who was supposed to be
 *  there. Neither Cobault nor the verifier can: we know a slot was claimed, not
 *  whether the right person claimed it.
 *
 *  One sentence, exported so the waiting state and the signing modal cannot
 *  drift. Deliberately NOT one of the two acknowledgement checkbox labels —
 *  those are verbatim-pinned (AR-21, COB-457) and are not touched here. */
export const ROSTER_VERIFICATION_NOTE =
  'Before you sign, check that the members listed on this vault are exactly ' +
  'the people you expect: a claim link admits whoever holds it, so this check ' +
  'is each signer’s own protection.';

/** AR-21 / R-INV-1c: the irretrievability acknowledgement gating activation.
 *
 *  Signing the activation manifest is the last checkpoint before the vault can
 *  receive bitcoin, and every quorum shape — not just `m == n` — shares one
 *  property the user must have named out loud: if the quorum can no longer be
 *  formed, nobody can rebuild it. TechCo holds no vault key, and a policy
 *  vault's `V` is additive and policy-bound, not a recovery key. The same
 *  house pattern as `WEB3AUTH_LOSS_ACK_LABEL`: an explicit sentence, checked
 *  before the primary action unlocks. Exported for the panel and its tests. */
export const ACTIVATION_IRRETRIEVABILITY_ACK_LABEL =
  'I understand that if a signing quorum can no longer be formed — through ' +
  'lost keys or unresponsive co-signers — the vault contents are ' +
  'irretrievable, and no one — including Cobault — can retrieve them on my ' +
  'behalf.';

/** COB-457 leg 1: the own-key backup acknowledgement gating activation.
 *
 *  The label above names the shared consequence; this one names the caller's
 *  own contribution to it. Activation is every participant's LAST touchpoint
 *  before the vault can receive bitcoin, so it is where "my key is backed up"
 *  has to be attested rather than assumed.
 *
 *  Deliberately agent-kind-neutral: "my recovery phrase, or the sign-in method
 *  I use with Cobault" covers a software/hardware seed AND a Web3Auth social
 *  login (which has no seed at all), so the same sentence is shown to every
 *  participant. Exported for the panel and its tests. */
export const ACTIVATION_KEY_BACKUP_ACK_LABEL =
  'I have backed up my own signing key — my recovery phrase, or the ' +
  'sign-in method I use with Cobault — and understand that losing it ' +
  'could leave this vault unable to form a signing quorum.';

/**
 * D2 signing-checkpoint modal: a WYSIWYS confirmation of the sealed vault
 * contract before the contributed key signs the activation manifest. Mirrors
 * the yield withdrawal modal's review-then-sign pattern. The deposit address is
 * the headline (where funds will go); the full descriptor + co-signer keys live
 * under a disclosure. The hard deposit-protection check (re-derive the address
 * from the roster + roster membership) is enforced by the key agent at sign
 * time — this modal is its human-readable companion, with no forgeable
 * "verified" claim.
 *
 * AR-21: `Sign & activate` is additionally gated on the irretrievability
 * acknowledgement. COB-457 adds a second, own-key backup acknowledgement — the
 * two are separate sentences because they are separate facts (the vault's
 * shared exposure vs. the caller's own contribution to it), and both must be
 * ticked. The checkbox state lives here rather than in the page, so it resets
 * on every open — the modal is conditionally mounted (`ackModalOpen && …`), so
 * closing it unmounts this component and the next open starts unticked. A
 * failed sign keeps the modal mounted (and the ticks) on purpose: the user is
 * retrying the same act, not re-consenting to a new one.
 */
export function ActivationSigningModal({
  bundle,
  error,
  signing,
  onConfirm,
  onCancel,
}: {
  bundle: SharedVaultActivationBundle | null;
  error: string | null;
  signing: boolean;
  onConfirm: (bundle: SharedVaultActivationBundle) => void;
  onCancel: () => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [backupAcknowledged, setBackupAcknowledged] = useState(false);
  return (
    <div class="shared-vault-modal-overlay" onClick={onCancel}>
      <div class="shared-vault-modal" onClick={(e) => e.stopPropagation()}>
        <div class="shared-vault-modal-eyebrow">
          <span class="shared-vault-modal-diamonds" aria-hidden="true">◇◆◇</span>
          You sign with your key
        </div>
        <h2 class="shared-vault-modal-title">Confirm this vault&rsquo;s contract</h2>
        <p class="shared-vault-modal-desc">
          Your browser re-derived this vault&rsquo;s deposit address from every
          co-signer&rsquo;s key. Signing this checkpoint commits your key to this
          exact contract — do it before any bitcoin is deposited.
        </p>

        {!bundle && !error && (
          <p class="accept-input-hint">Loading the sealed contract…</p>
        )}

        {bundle && (
          <>
            <div class="shared-vault-modal-field">
              <div class="shared-vault-modal-metric-label">
                Deposit address — where funds will go
              </div>
              <div class="shared-vault-modal-address mono">
                {bundle.deposit_address}
              </div>
            </div>
            <div class="shared-vault-modal-grid">
              <div class="shared-vault-modal-metric">
                <div class="shared-vault-modal-metric-label">
                  Signatures to spend
                </div>
                <div class="shared-vault-modal-metric-value tabular-nums">
                  {bundle.quorum_m} of {activationSignerCount(bundle)}
                </div>
              </div>
              <div class="shared-vault-modal-metric">
                <div class="shared-vault-modal-metric-label">Network</div>
                <div class="shared-vault-modal-metric-value">{bundle.network}</div>
              </div>
            </div>
            {bundle.verifier_pubkey_hex && (
              <div class="shared-vault-modal-field">
                <div class="shared-vault-modal-metric-label">
                  Allowlist policy
                </div>
                <p class="shared-vault-modal-desc">
                  This vault includes the Cobault verifier as an extra co-signer.
                  It will co-sign withdrawals only to allow-listed addresses — it
                  cannot move funds on its own, and your co-signers can still
                  reach quorum without it.
                </p>
              </div>
            )}
            {/* COB-543 (D7): prominent — outside the disclosure, above the
                keys it is about. The roster is the only surface on which a
                misdirected claim link becomes visible, and this signature is
                the last checkpoint before the vault can hold bitcoin. */}
            <p
              class="shared-vault-modal-desc shared-vault-modal-warn"
              data-testid="sv-roster-verification-note"
            >
              {ROSTER_VERIFICATION_NOTE}
            </p>
            <details class="shared-vault-modal-details">
              <summary>
                <span class="shared-vault-modal-chevron" aria-hidden="true">▸</span>
                <span>Full descriptor &amp; co-signer keys</span>
              </summary>
              <div class="shared-vault-modal-details-body">
                <div class="shared-vault-modal-metric-label">Descriptor</div>
                <div class="shared-vault-modal-address mono">
                  {bundle.multisig_descriptor}
                </div>
                <div class="shared-vault-modal-metric-label">
                  Co-signer keys ({bundle.roster.length})
                </div>
                {bundle.roster.map((k) => (
                  <div key={k} class="shared-vault-modal-address mono">
                    {k}
                  </div>
                ))}
                {bundle.verifier_pubkey_hex && (
                  <>
                    <div class="shared-vault-modal-metric-label">
                      Verifier co-signer key
                    </div>
                    <div class="shared-vault-modal-address mono">
                      {bundle.verifier_pubkey_hex}
                    </div>
                    <div class="shared-vault-modal-metric-label">
                      Policy contract hash
                    </div>
                    <div class="shared-vault-modal-address mono">
                      {bundle.contract_hash_hex}
                    </div>
                  </>
                )}
              </div>
            </details>
          </>
        )}

        {error && <p class="shared-vault-modal-error">{error}</p>}

        <label class="loss-ack-row">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) =>
              setAcknowledged((e.target as HTMLInputElement).checked)
            }
            disabled={signing}
          />
          <span>{ACTIVATION_IRRETRIEVABILITY_ACK_LABEL}</span>
        </label>

        <label class="loss-ack-row">
          <input
            type="checkbox"
            checked={backupAcknowledged}
            onChange={(e) =>
              setBackupAcknowledged((e.target as HTMLInputElement).checked)
            }
            disabled={signing}
          />
          <span>{ACTIVATION_KEY_BACKUP_ACK_LABEL}</span>
        </label>

        <div class="shared-vault-modal-actions">
          <button class="btn btn-secondary" onClick={onCancel} disabled={signing}>
            Cancel
          </button>
          <button
            class="btn btn-primary"
            onClick={() => bundle && onConfirm(bundle)}
            disabled={!bundle || signing || !acknowledged || !backupAcknowledged}
          >
            {signing ? 'Signing…' : 'Sign & activate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Agreed-policy summary section (COB-382) ───────────────────────────

/** COB-382: the agreed-policy summary on the vault detail page. Renders for BOTH
 *  vault flavours: a policy vault shows its verifier allowlist (each address via
 *  <AddressReveal>, the same truncated-and-copyable treatment the pending-row
 *  destinations use) plus the configured authorisation-rate ceiling; a
 *  peer-to-peer vault shows an explicit no-policy line rather than an absent
 *  section. Display-only — the allowlist's other uses (the off-allowlist
 *  advisories on the send form + pending rows) are unchanged; this section only
 *  surfaces the agreed policy. Every render branch is driven by an exported pure
 *  helper (`policySummaryView` / `policyLimitLine` / `allowlistHeading`). */
export function PolicySummarySection({
  vault,
}: {
  vault: SharedVaultDetailResponse;
}) {
  const view = policySummaryView(vault);

  if (view.kind === 'p2p') {
    return (
      <section class="shared-vault-section">
        <h2 class="shared-vault-section-title">Spending policy</h2>
        <p class="accept-input-hint">
          No spending policy — any quorum of participants can withdraw to any
          address.
        </p>
      </section>
    );
  }

  const limitLine = policyLimitLine(view.limit);
  return (
    <section class="shared-vault-section">
      <h2 class="shared-vault-section-title">Spending policy</h2>
      <p class="accept-input-hint">
        Withdrawals can be co-signed by the Cobault verifier only to this vault's
        allowlisted addresses. Participants can still send anywhere by reaching a
        full signature quorum without the verifier.
      </p>
      <div class="sv-policy-allowlist">
        <div class="sv-policy-label">
          {allowlistHeading(view.allowlist.length)}
        </div>
        {view.allowlist.length > 0 ? (
          <ul class="sv-allowlist">
            {view.allowlist.map((addr) => (
              <li key={addr} class="sv-allowlist-item">
                <AddressReveal address={addr} />
              </li>
            ))}
          </ul>
        ) : (
          <p class="accept-input-hint">No allowlisted addresses on record.</p>
        )}
      </div>
      {limitLine && <p class="shared-vault-balance tabular-nums">{limitLine}</p>}
    </section>
  );
}

// ── Independent-verifier review link (COB-507) ────────────────────────

/**
 * COB-507: the participant's route to the independent verifier's own copy of
 * this vault's policy — the arm's-length record, rendered by verifier-shared
 * at `{origin}/v/{vault_id}` rather than mirrored here.
 *
 * Structurally the shared-vault twin of the yield page's `VerifierConfirmField`
 * (vault-detail.tsx), with two deliberate differences:
 *
 *  - **No embed, no confirm-state tracking.** This is a *review* link, not a
 *    ceremony: nothing on the user-app side is recorded when a participant
 *    follows it, and no state changes. The yield field doubles as the
 *    pre-deposit contract-confirmation step; this one does not.
 *  - **Participant-voiced, not owner-voiced.** A shared vault has `n` humans
 *    and any one of their keys unlocks the view (that is what the
 *    `cobault-verifier-shared-ownership-v1` proof establishes), so the copy
 *    speaks to "any participant" rather than to an owner.
 *
 * The link is what makes the configured-limit mirror on this page honest: the
 * verifier's page is where the live headroom against its own signed-amount
 * ledger is shown, because the verifier is the party that holds the ledger.
 *
 * Exported for the render tests.
 */
export function VerifierReviewSection({ reviewUrl }: { reviewUrl: string }) {
  return (
    <section class="shared-vault-section">
      <h2 class="shared-vault-section-title">Independent verifier</h2>
      <p class="accept-input-hint">
        Confirm the policy the independent verifier holds for this vault — any
        participant key unlocks the view. It is the verifier's own record, on
        the verifier's own site, and it shows how much of any rolling limit is
        still available.
      </p>
      <p class="vault-verifier-confirm-alt">
        <a
          class="vault-field-link"
          href={reviewUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Review policy on the independent verifier ↗
        </a>
      </p>
    </section>
  );
}

// ── Send (B5 — initiate a withdrawal) ─────────────────────────────────

function SendSection({
  rows,
  quorumM,
  n,
  feeLadder,
  feeSelection,
  sending,
  canSend,
  awaitingHint,
  showVerifierOption,
  allowlist,
  useVerifier,
  onUseVerifierChange,
  onRowsChange,
  onFeeSelectionChange,
  onSend,
  msg,
}: {
  rows: SendRow[];
  /** COB-485: the vault's signing threshold and roster size — the hint states
   *  where co-signer review actually stops (`sendCosignerHint`). */
  quorumM: number;
  n: number;
  /** COB-485: the served ladder (bare or per-draft quote), or `null` while
   *  the first fetch is in flight. */
  feeLadder: FeeLadderView | null;
  feeSelection: FeeSelection;
  sending: boolean;
  canSend: boolean;
  /** COB-354: informational line when the vault's balance is all still
   *  confirming (spendable balance is zero), or `null`. */
  awaitingHint: string | null;
  showVerifierOption: boolean;
  /** COB-362: the vault's verifier allowlist (display mirror), or `null` on a
   *  no-policy vault — drives the non-blocking creation-time off-allowlist
   *  advisory when verifier co-signing is on. */
  allowlist: string[] | null;
  useVerifier: boolean;
  onUseVerifierChange: (v: boolean) => void;
  onRowsChange: (rows: SendRow[]) => void;
  onFeeSelectionChange: (next: FeeSelection) => void;
  onSend: () => void;
  /** COB-352: the Create-withdrawal outcome, rendered inline beside the button.
   *  COB-362: a success banner also carries the created withdrawal's `id`. */
  msg: { kind: 'error' | 'success'; text: string; id?: string } | null;
}) {
  function update(i: number, patch: Partial<SendRow>) {
    onRowsChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() {
    onRowsChange([...rows, blankSendRow()]);
  }
  function removeRow(i: number) {
    onRowsChange(rows.length === 1 ? [blankSendRow()] : rows.filter((_, idx) => idx !== i));
  }
  return (
    <section class="shared-vault-section">
      <h2 class="shared-vault-section-title">Send</h2>
      <p class="accept-input-hint">{sendCosignerHint(quorumM, n)}</p>
      {awaitingHint && <p class="shared-vault-notice">{awaitingHint}</p>}
      {rows.map((row, i) => (
        <Fragment key={i}>
        <div class="shared-vault-send-row">
          <input
            class="accept-input"
            type="text"
            placeholder="Recipient address"
            value={row.address}
            disabled={sending}
            onInput={(e) =>
              update(i, { address: (e.target as HTMLInputElement).value })
            }
          />
          <input
            class="accept-input shared-vault-send-amount"
            type="text"
            inputMode="decimal"
            placeholder="Amount (BTC)"
            value={row.amount}
            disabled={sending}
            onInput={(e) =>
              update(i, { amount: (e.target as HTMLInputElement).value })
            }
          />
          <button
            type="button"
            class="btn btn-danger btn-sm"
            disabled={sending}
            onClick={() => removeRow(i)}
          >
            Remove
          </button>
        </div>
        {/* COB-362: non-blocking creation-time advisory — with verifier
            co-signing on, warn when this recipient is off the allowlist. It
            never disables the Create button. */}
        {sendRowOffAllowlist(row, useVerifier, allowlist) && (
          <p class="accept-input-hint sv-allowlist-warn">
            This address isn't on the verifier allowlist — the verifier won't
            co-sign. You can still send it if enough participants sign, or switch
            off verifier co-signing.
          </p>
        )}
        </Fragment>
      ))}
      <button
        type="button"
        class="btn btn-link"
        disabled={sending}
        onClick={addRow}
      >
        + Add recipient
      </button>
      <FeeSelector
        ladder={feeLadder}
        selection={feeSelection}
        onChange={onFeeSelectionChange}
        disabled={sending}
      />
      {showVerifierOption && (
        <div class="shared-vault-verifier-option">
          <button
            type="button"
            role="switch"
            aria-checked={useVerifier}
            class="sv-switch"
            disabled={sending}
            onClick={() => onUseVerifierChange(!useVerifier)}
          >
            <span class="sv-switch-track" aria-hidden="true">
              <span class="sv-switch-thumb" />
            </span>
            <span class="sv-switch-text">
              <span class="sv-switch-label">Verifier co-signing</span>
              <span class="sv-switch-desc">
                The Cobault verifier co-signs only to this vault’s allowlisted
                addresses and counts as one signature toward the threshold —
                optional, your participants can always complete a withdrawal
                without it.
              </span>
            </span>
          </button>
        </div>
      )}
      <button
        type="button"
        class="btn btn-primary"
        disabled={sending || !canSend}
        onClick={onSend}
      >
        {sending ? 'Creating…' : 'Create withdrawal'}
      </button>
      {msg && (
        <p class={msg.kind === 'error' ? 'vault-card-error' : 'shared-vault-notice'}>
          {msg.text}
        </p>
      )}
    </section>
  );
}

// ── Pending withdrawal row (B5 — co-signer Sign) ──────────────────────

function PendingTxRow({
  tx,
  participants,
  allowlist,
  feeLadder,
  busy,
  signMsg,
  cancelMsg,
  canCancel,
  onSign,
  onCancel,
}: {
  tx: SharedPendingTx;
  /** COB-362: the vault roster — drives the per-party signing-status list
   *  (each participant marked signed / pending from `tx.signed_slots`). */
  participants: SharedVaultParticipant[];
  /** COB-362: the vault's verifier allowlist (display mirror), or `null` on a
   *  no-policy vault — drives the "Not on verifier allowlist" advisory tag. */
  allowlist: string[] | null;
  /** COB-485: the page's current served fee ladder, or `null` before the first
   *  fetch — the live reference the row's stale-rate advisory compares against
   *  (`pendingTxFeeRateWarning`). Advisory only; it never gates signing. */
  feeLadder: FeeLadderView | null;
  busy: boolean;
  /** COB-350: this row's last "Review & sign" outcome, or null. */
  signMsg: { kind: 'error' | 'success'; text: string } | null;
  /** COB-461: this row's last Cancel outcome, or null. */
  cancelMsg: { kind: 'error' | 'success'; text: string } | null;
  /** COB-461: whether to offer the Cancel button — `pendingTxCancellable`, a
   *  DISPLAY heuristic (the server gates on membership, never on age). */
  canCancel: boolean;
  onSign: () => void;
  onCancel: () => void;
}) {
  const total = tx.outputs.reduce((sum, o) => sum + o.amount_sats, 0);
  // COB-357: the state-driven verifier disposition (tag + optional detail line).
  // COB-362: the neutral cosigns arm is superseded by an off-allowlist advisory.
  const verifierView = pendingTxVerifierViewWithAllowlist(tx, allowlist);
  // COB-485: the stale-rate advisory. Warn-only — it changes nothing about
  // whether the Sign button renders or what it does.
  const feeRateWarning = pendingTxFeeRateWarning(tx, feeLadder);
  // COB-356: the row is an explicit vertical stack of separated zones — status,
  // outputs, status lines, then a right-aligned action zone of real buttons.
  return (
    <li class="shared-vault-pending-row">
      {/* Status zone — the status badge (tone-coded by lifecycle), the
          amount·fee, and the "opted into verifier co-signing" descriptor. */}
      <div class="shared-vault-pending-head">
        <span
          class={`shared-vault-badge shared-vault-badge--${pendingTxStatusTone(
            tx.status,
          )}`}
        >
          {/* COB-485: honest human copy, not the raw enum. The wire value is
              unchanged — this is render-layer mapping only. */}
          {pendingTxStatusLabel(tx.status)}
        </span>
        <span class="tabular-nums">{formatBtc(total)} BTC to recipients</span>
        {verifierView.kind !== 'none' && (
          <span class={`shared-vault-tag ${verifierView.tagModifier}`}>
            {/* COB-358: the awaiting state carries a pulsing dot so the row reads
                as actively engaging the verifier, not a static neutral tag. */}
            {verifierView.kind === 'awaiting' && (
              <span class="sv-verifier-pulse" aria-hidden="true" />
            )}
            {verifierView.tag}
          </span>
        )}
        {tx.txid && (
          <span class="vault-card-id mono">{truncate(tx.txid, 12)}</span>
        )}
      </div>
      {/* Outputs zone. COB-359: each destination is a click-to-reveal address —
          truncated at rest so the row stays scannable, expandable to the full
          address with a Copy affordance. */}
      <ul class="shared-vault-output-list">
        {tx.outputs.map((o, i) => (
          <li key={i} class="shared-vault-output-row">
            <AddressReveal address={o.address} />
            <span class="tabular-nums">{formatBtc(o.amount_sats)} BTC</span>
          </li>
        ))}
      </ul>
      {/* Fee zone (COB-485 Ticket C) — the pre-sign summary. A shared-vault
          fee is paid out of vault value (AR-3), so it is part of what a
          signature authorises; it is stated in sats, BTC and the initiator's
          chosen sat/vB rate rather than one rounded BTC figure. Inline, above
          the Sign button — the clear-sign ceremony in the key agent is still
          the confirmation step, and this adds no second modal in front of it.
          The advisory below is warn-only and never gates the button. */}
      <p class="shared-vault-pending-fee tabular-nums">
        {pendingTxFeeSummary(tx)}
      </p>
      {feeRateWarning && (
        <p class="accept-input-hint sv-allowlist-warn">{feeRateWarning}</p>
      )}
      {/* Signers zone (COB-362) — a compact per-party signing roster: each
          participant attributed from `signed_slots`, with the verifier appended
          when the withdrawal opted in. Tag text + tint come from the helper
          (status-aware: Pending only while COLLECTING; Did not sign on terminal
          rows; a REFUSED verifier reads Declined). */}
      <ul class="sv-signer-status">
        {pendingTxSignerAttribution(tx, participants).map((s) => (
          <li key={s.key} class="sv-signer-status-item">
            <span class="sv-signer-email">{s.label}</span>
            <span
              class={`shared-vault-tag${s.tagModifier ? ` ${s.tagModifier}` : ''}`}
            >
              {s.tag}
            </span>
          </li>
        ))}
      </ul>
      {/* Status-line zone — the signed-state line (COB-352), the verifier
          refusal detail (COB-357 — verbatim reason + R-INV-1 reassurance, kept
          out of the pill since reasons can be long), and this row's last
          "Review & sign" outcome (COB-350). */}
      {pendingTxSignedByMe(tx) && (
        <p class="shared-vault-notice">{pendingTxSignedLabel(tx)}</p>
      )}
      {tx.status === 'CANCELLED' && (
        <p class="shared-vault-notice">{pendingTxCancelledLabel(tx)}</p>
      )}
      {(verifierView.kind === 'declined' ||
        verifierView.kind === 'off_allowlist') && (
        <p class="shared-vault-notice">{verifierView.detail}</p>
      )}
      {signMsg && (
        <p
          class={
            signMsg.kind === 'error' ? 'vault-card-error' : 'shared-vault-notice'
          }
        >
          {signMsg.text}
        </p>
      )}
      {cancelMsg && (
        <p
          class={
            cancelMsg.kind === 'error'
              ? 'vault-card-error'
              : 'shared-vault-notice'
          }
        >
          {cancelMsg.text}
        </p>
      )}
      {/* Action zone — right-aligned, real buttons only. Verifier co-signing is
          no longer a manual per-row request (COB-356): a backend watcher engages
          it, so the row offers no "Request verifier" action.
          COB-461: Cancel is secondary and sits after Sign — the two can be
          offered together on a COLLECTING row, and signing is the expected act. */}
      {(pendingTxAwaitingMySignature(tx) || canCancel) && (
        <div class="shared-vault-pending-actions">
          {pendingTxAwaitingMySignature(tx) && (
            <button
              type="button"
              class="btn btn-primary"
              disabled={busy}
              onClick={onSign}
            >
              {busy ? 'Signing…' : 'Review & sign'}
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              class="btn btn-secondary"
              disabled={busy}
              onClick={onCancel}
            >
              {busy ? 'Cancelling…' : 'Cancel withdrawal'}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

// ── On-chain transaction history (COB-225) ────────────────────────────

/** The vault deposit address's on-chain history, rendered with the same
 *  `TransactionList` the personal wallet uses. Fetched from the membership-gated
 *  `/shared-vault/:id/txs` route; only shown once an address is sealed. */
function SharedVaultTransactions({ vaultId }: { vaultId: string }) {
  const [txs, setTxs] = useState<WalletTx[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api
      .getSharedVaultTxs(vaultId)
      .then(setTxs)
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Failed to load transactions.'),
      );
  }, [vaultId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section class="shared-vault-section">
      <h2 class="shared-vault-section-title">Transactions</h2>
      {error && <p class="vault-card-error">{error}</p>}
      {txs === null && !error && <p class="accept-input-hint">Loading…</p>}
      {txs !== null && txs.length === 0 && (
        <p class="accept-input-hint">No transactions yet.</p>
      )}
      {txs !== null && txs.length > 0 && <TransactionList txs={txs} />}
      <button type="button" class="btn btn-link" onClick={load}>
        Refresh
      </button>
    </section>
  );
}

// ── Descriptor / spec export (COB-499) ────────────────────────────────

/** Hand a string to the browser as a file download. The only impure half of
 *  the export — the file body itself is built by the pure helpers in
 *  `descriptor-export.ts`. */
function downloadTextFile(body: string, filename: string, mime: string) {
  const url = URL.createObjectURL(new Blob([body], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Deferred by a tick: revoking in the same turn as the click can cancel the
  // download before the browser has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** The vault-spec download: everything needed to watch or recreate this vault
 *  outside Cobault. On a peer-to-peer (no-policy) vault this file is the
 *  complete exit — the descriptor it carries is plain Bitcoin infrastructure
 *  that no Cobault service participates in.
 *
 *  Exported for the render tests. */
export function DescriptorExportSection({
  exporting,
  error,
  onDownload,
}: {
  exporting: boolean;
  error: string | null;
  onDownload: () => void;
}) {
  return (
    <section class="shared-vault-section">
      <h2 class="shared-vault-section-title">Vault spec</h2>
      <p class="accept-input-hint">
        Download this shared vault’s spec for backup — watch or recreate it
        outside Cobault as native Bitcoin infrastructure.
      </p>
      {error && <p class="vault-card-error">{error}</p>}
      <button
        type="button"
        class="btn btn-secondary sv-spec-download"
        disabled={exporting}
        onClick={onDownload}
      >
        {exporting ? 'Preparing…' : 'Download vault spec'}
      </button>
      <ul class="sv-spec-caveats">
        <li>
          Importing into a wallet app? Sparrow and most wallets take the
          file’s <code class="mono">wallet_import_descriptor</code> field —
          the <code class="mono">descriptor</code> field is the fixed-key form
          for Bitcoin Core.
        </li>
        <li>
          The file holds no private keys, but it reveals every co-signer’s
          public keys and makes this vault watchable. Keep it private.
        </li>
        <li>
          Only this vault’s deposit address belongs to it — other addresses the
          descriptor derives are not watched by Cobault or the verifier.
        </li>
      </ul>
    </section>
  );
}

// ── Participants section ──────────────────────────────────────────────

/** The vault roster: the human participants, followed — on a policy vault — by
 *  a single non-interactive row for the additive verifier key `V` the sealed
 *  descriptor folds in. Extracted so the verifier-row composition is unit-
 *  testable in isolation. */
export function ParticipantsSection({
  vault,
  manageInvites,
  busySlot,
  issuedLinks,
  onRegenerate,
  onDismissLink,
  onRemove,
}: {
  vault: SharedVaultDetailResponse;
  manageInvites: boolean;
  busySlot: string | null;
  /** COB-543: claim links minted this session, by slot_id. A slot with an entry
   *  renders it inline — the one and only chance to read that link. */
  issuedLinks: Record<string, IssuedInvite>;
  onRegenerate: (slotId: string) => void;
  onDismissLink: (slotId: string) => void;
  onRemove: (slotId: string) => void;
}) {
  return (
    <section class="shared-vault-section">
      <h2 class="shared-vault-section-title">Participants</h2>
      <ul class="shared-vault-participants-list">
        {vault.participants.map((p) => (
          <ParticipantRow
            key={p.slot_id}
            participant={p}
            manageable={manageInvites && canManageInvite(p)}
            busy={busySlot === p.slot_id}
            issuedLink={issuedLinks[p.slot_id] ?? null}
            onRegenerate={(pp) => onRegenerate(pp.slot_id)}
            onDismissLink={(pp) => onDismissLink(pp.slot_id)}
            onRemove={(pp) => onRemove(pp.slot_id)}
          />
        ))}
        {vault.has_policy && <VerifierParticipantRow />}
      </ul>
    </section>
  );
}

/** The additive verifier key `V` shown as a roster member on a policy vault. It
 *  is not a human participant — it carries no invite/acknowledge status and can
 *  never move funds on its own (R-INV-1) — so it renders as a non-interactive,
 *  clearly-tagged machine co-signer after the human roster. */
function VerifierParticipantRow() {
  return (
    <li class="shared-vault-participant-row">
      <div class="shared-vault-participant-main">
        <span class="shared-vault-participant-email">Cobault Verifier</span>
        <span class="shared-vault-participant-tags">
          <span class="shared-vault-tag shared-vault-tag--verifier">
            (verifier)
          </span>
        </span>
      </div>
    </li>
  );
}

// ── Participant row ───────────────────────────────────────────────────

function ParticipantRow({
  participant,
  manageable,
  busy,
  issuedLink,
  onRegenerate,
  onDismissLink,
  onRemove,
}: {
  participant: SharedVaultParticipant;
  manageable: boolean;
  busy: boolean;
  /** COB-543: a claim link minted for this slot in this session, or `null`. */
  issuedLink: IssuedInvite | null;
  onRegenerate: (p: SharedVaultParticipant) => void;
  onDismissLink: (p: SharedVaultParticipant) => void;
  onRemove: (p: SharedVaultParticipant) => void;
}) {
  const name = participantDisplayName(participant);
  return (
    <li class="shared-vault-participant-row">
      <div class="shared-vault-participant-main">
        <span
          class={`shared-vault-participant-email${
            name.muted ? ' shared-vault-participant-email--muted' : ''
          }`}
        >
          {name.text}
        </span>
        <span class="shared-vault-participant-tags">
          {participant.is_initiator && (
            <span class="shared-vault-tag">Initiator</span>
          )}
          <span
            class={`shared-vault-tag shared-vault-tag--${participant.status.toLowerCase()}`}
          >
            {participantStatusLabel(participant.status)}
          </span>
          {participant.acknowledged && (
            <span class="shared-vault-tag shared-vault-tag--ack">Signed</span>
          )}
          {manageable && (
            <span class="shared-vault-invite-actions">
              {/* COB-543: "New link", not "Resend" — nothing is sent, and the
                  previous link stops working the instant this returns. */}
              <button
                type="button"
                class="btn btn-secondary btn-sm"
                disabled={busy}
                onClick={() => onRegenerate(participant)}
              >
                {busy ? 'Generating…' : 'New link'}
              </button>
              <button
                type="button"
                class="btn btn-danger btn-sm"
                disabled={busy}
                onClick={() => onRemove(participant)}
              >
                Remove
              </button>
            </span>
          )}
        </span>
      </div>
      {/* COB-543: the minted link, inline on its own row. This is the only place
          it will ever be legible — dismissing it discards it for good, which is
          why the control says so rather than reading as a tidy-up. */}
      {issuedLink && (
        <div class="shared-vault-participant-link">
          <p class="accept-error sv-link-warning">
            {CLAIM_LINK_BEARER_WARNING_ONE}
          </p>
          <ClaimLinkCard
            invite={issuedLink}
            title={issuedLink.label ?? 'Unlabelled slot'}
          />
          <button
            type="button"
            class="btn btn-link"
            onClick={() => onDismissLink(participant)}
          >
            I've copied it — hide this link
          </button>
        </div>
      )}
    </li>
  );
}
